import { WordItem } from '../types';

export interface GrammarEntry {
  gender?: 'm' | 'f';
  singularKab?: string;
  singularFr?: string;
  pluralKab?: string;
  pluralFr?: string;
  feminineKab?: string;
  masculineKab?: string;
  variants?: string;
  notes?: string;
  conjugationAccompli?: string;
  conjugationInaccompli?: string;
}

/**
 * Dictionnaire grammatical kabyle de référence :
 * Regroupe les formes singulier / pluriel et masculin / féminin
 * des mots les plus courants et utilisés dans l'application.
 */
export const KABYLE_GRAMMAR_DB: Record<string, GrammarEntry> = {
  // --- Verbes et Formes de Conjugaison (Accompli & Inaccompli) ---
  'ečč': {
    conjugationInaccompli: 'Tteččeɣ (Je mange) | Itečč (Il mange)',
    conjugationAccompli: 'Ččiɣ (J\'ai mangé) | Yečča (Il a mangé)',
  },
  'sew': {
    conjugationInaccompli: 'Ttesseɣ (Je bois) | Ittess (Il boit)',
    conjugationAccompli: 'Swiɣ (J\'ai bu) | Yeswa (Il a bu)',
  },
  'gen': {
    conjugationInaccompli: 'Gganeɣ (Je dors) | Iggan (Il dort)',
    conjugationAccompli: 'Gneɣ (J\'ai dormi) | Yegen (Il a dormi)',
  },
  'kker': {
    conjugationInaccompli: 'Tekkireɣ (Je me lève) | Ittekkir (Il se lève)',
    conjugationAccompli: 'Kkreɣ (Je me suis levé) | Yekker (Il s\'est levé)',
  },
  'kker-d': {
    conjugationInaccompli: 'Tekkireɣ-d (Je me réveille) | Ittekkir-d (Il se réveille)',
    conjugationAccompli: 'Kkreɣ-d (Je me suis réveillé) | Yekker-d (Il s\'est réveillé)',
  },
  'ruḥ': {
    conjugationInaccompli: 'Ttruḥuɣ (Je pars) | Ittruḥu (Il part)',
    conjugationAccompli: 'Ruḥeɣ (Je suis parti) | Yeruḥ (Il est parti)',
  },
  'as-d': {
    conjugationInaccompli: 'Ttaseɣ-d (Je viens) | Ittas-d (Il vient)',
    conjugationAccompli: 'Usiɣ-d (Je suis venu) | Yusa-d (Il est venu)',
  },
  'uɣal-d': {
    conjugationInaccompli: 'Ttuɣaleɣ-d (Je reviens) | Ittuɣal-d (Il revient)',
    conjugationAccompli: 'Uɣaleɣ-d (Je suis revenu) | Yuɣal-d (Il est revenu)',
  },
  'ini': {
    conjugationInaccompli: 'Qqareɣ (Je dis) | Iqqar (Il dit)',
    conjugationAccompli: 'Nniɣ (J\'ai dit) | Yenna (Il a dit)',
  },
  'hder': {
    conjugationInaccompli: 'Theddireɣ (Je parle) | Itheddir (Il parle)',
    conjugationAccompli: 'Hedreɣ (J\'ai parlé) | Yehder (Il a parlé)',
  },
  'ssawel': {
    conjugationInaccompli: 'Ssawaleɣ (J\'appelle) | Isawal (Il appelle)',
    conjugationAccompli: 'Ssawleɣ (J\'ai appelé) | Yessawel (Il a appelé)',
  },
  'af': {
    conjugationInaccompli: 'Ttafeɣ (Je trouve) | Ittaf (Il trouve)',
    conjugationAccompli: 'Ufiɣ (J\'ai trouvé) | Yufa (Il a trouvé)',
  },
  'fk': {
    conjugationInaccompli: 'Ttakeɣ (Je donne) | Ittak (Il donne)',
    conjugationAccompli: 'Fkiɣ (J\'ai donné) | Yefka (Il a donné)',
  },
  'aɣ': {
    conjugationInaccompli: 'Ttaɣeɣ (J\'achète) | Ittaɣ (Il achète)',
    conjugationAccompli: 'Uɣeɣ (J\'ai acheté) | Yuɣ (Il a acheté)',
  },
  'zenzen': {
    conjugationInaccompli: 'Zenzayeɣ (Je vends) | Izenzay (Il vend)',
    conjugationAccompli: 'Zenzneɣ (J\'ai vendu) | Izenzen (Il a vendu)',
  },
  'xdem': {
    conjugationInaccompli: 'Xeddmeɣ (Je travaille) | Ixeddem (Il travaille)',
    conjugationAccompli: 'Xedmeɣ (J\'ai travaillé) | Yexdem (Il a travaillé)',
  },
  'lmed': {
    conjugationInaccompli: 'Lemmdeɣ (J\'apprends) | Ilemmed (Il apprend)',
    conjugationAccompli: 'Lmdeɣ (J\'ai appris) | Yelmed (Il a appris)',
  },
  'slemd': {
    conjugationInaccompli: 'Sselmadeɣ (J\'enseigne) | Isselmad (Il enseigne)',
    conjugationAccompli: 'Sselmdeɣ (J\'ai enseigné) | Yesselmed (Il a enseigné)',
  },
  'aru': {
    conjugationInaccompli: 'Ttaruɣ (J\'écris) | Ittaru (Il écrit)',
    conjugationAccompli: 'Uriɣ (J\'ai écrit) | Yura (Il a écrit)',
  },
  'ɣer': {
    conjugationInaccompli: 'Qqareɣ (Je lis) | Iqqar (Il lit)',
    conjugationAccompli: 'Ɣriɣ (J\'ai lu) | Yeɣra (Il a lu)',
  },
  'fhem': {
    conjugationInaccompli: 'Fehhmeɣ (Je comprends) | Ifehhem (Il comprend)',
    conjugationAccompli: 'Fhmeɣ (J\'ai compris) | Yefhem (Il a compris)',
  },
  'wal': {
    conjugationInaccompli: 'Ttwaliɣ (Je vois) | Ittwali (Il voit)',
    conjugationAccompli: 'Walaɣ (J\'ai vu) | Yewala (Il a vu)',
  },
  'ssell': {
    conjugationInaccompli: 'Sselleɣ (J\'écoute) | Isell (Il écoute)',
    conjugationAccompli: 'Sliɣ (J\'ai écouté) | Yesla (Il a écouté)',
  },
  'ldi': {
    conjugationInaccompli: 'Tteldiyen (J\'ouvre) | Itteldi (Il ouvre)',
    conjugationAccompli: 'Ldiɣ (J\'ai ouvert) | Yeldi (Il a ouvert)',
  },
  'mdel': {
    conjugationInaccompli: 'Meddleɣ (Je ferme) | Imeddel (Il ferme)',
    conjugationAccompli: 'Mdleɣ (J\'ai fermé) | Yemdel (Il a fermé)',
  },
  'bedd': {
    conjugationInaccompli: 'Ttebbdideɣ (Je me tiens debout) | Ibedded (Il se tient debout)',
    conjugationAccompli: 'Bdedeɣ (Je me suis levé) | Yebded (Il s\'est levé)',
  },
  'qim': {
    conjugationInaccompli: 'Tteɣimiɣ (Je m\'assieds) | Ittɣimi (Il s\'assoit)',
    conjugationAccompli: 'Qqimeɣ (Je me suis assis) | Yeqqim (Il s\'est assis)',
  },
  'rwel': {
    conjugationInaccompli: 'Trewwleɣ (Je m\'enfuis) | Irewwel (Il s\'enfuit)',
    conjugationAccompli: 'Rwleɣ (Je me suis enfui) | Yerwel (Il s\'est enfui)',
  },
  'tabaɛ': {
    conjugationInaccompli: 'Ttettabaɛeɣ (Je suis) | Itettabaɛ (Il suit)',
    conjugationAccompli: 'Tabaɛeɣ (J\'ai suivi) | Itabaɛ (Il a suivi)',
  },
  'refd': {
    conjugationInaccompli: 'Reffdeɣ (Je soulève) | Irefded (Il soulève)',
    conjugationAccompli: 'Rfdeɣ (J\'ai soulevé) | Yerfed (Il a soulevé)',
  },
  'sers': {
    conjugationInaccompli: 'Sserseɣ (Je pose) | Iserras (Il pose)',
    conjugationAccompli: 'Sserseɣ (J\'ai posé) | Yessers (Il a posé)',
  },
  'kkes': {
    conjugationInaccompli: 'Ttekkseɣ (J\'enlève) | Ittekkes (Il enlève)',
    conjugationAccompli: 'Kkseɣ (J\'ai enlevé) | Yekkes (Il a enlevé)',
  },
  'rnu': {
    conjugationInaccompli: 'Tterniɣ (J\'ajoute) | Ittnerni (Il ajoute)',
    conjugationAccompli: 'Rniɣ (J\'ai ajouté) | Yerna (Il a ajouté)',
  },
  'neqqes': {
    conjugationInaccompli: 'Neqqseɣ (Je diminue) | Ineqqas (Il diminue)',
    conjugationAccompli: 'Neqqseɣ (J\'ai diminué) | Ineqqes (Il a diminué)',
  },
  'ḥader': {
    conjugationInaccompli: 'Teḥdareɣ (Je fais attention) | Iteḥdar (Il fait attention)',
    conjugationAccompli: 'Ḥadreɣ (J\'ai fait attention) | Iḥader (Il a fait attention)',
  },
  'ḥrez': {
    conjugationInaccompli: 'Ḥerrizeɣ (Je garde) | Iḥerrez (Il garde)',
    conjugationAccompli: 'Ḥerzeɣ (J\'ai gardé) | Yeḥrez (Il a gardé)',
  },
  'fren': {
    conjugationInaccompli: 'Ferrneɣ (Je choisis) | Iferren (Il choisit)',
    conjugationAccompli: 'Ferneɣ (J\'ai choisi) | Yefren (Il a choisi)',
  },
  'bdu': {
    conjugationInaccompli: 'Ttbadaɣ (Je commence) | Ittbada (Il commence)',
    conjugationAccompli: 'Bdiɣ (J\'ai commencé) | Yebda (Il a commencé)',
  },
  'kfu': {
    conjugationInaccompli: 'Tteffaɣ (Je termine) | Itteffa (Il termine)',
    conjugationAccompli: 'Kfiɣ (J\'ai terminé) | Yekfa (Il a terminé)',
  },
  'snuwwi': {
    conjugationInaccompli: 'Ssnuwwuyeɣ (Je cuisine) | Isnuwwuy (Il cuisine)',
    conjugationAccompli: 'Snuwwiɣ (J\'ai cuisiné) | Yessnuwwa (Il a cuisiné)',
  },
  'sebbeɣ': {
    conjugationInaccompli: 'Ssebbayeɣ (Je peins) | Isebbay (Il peint)',
    conjugationAccompli: 'Sebbɣeɣ (J\'ai peint) | Isebbeɣ (Il a peint)',
  },
  'ffer': {
    conjugationInaccompli: 'Tteffreɣ (Je cache) | Iteffer (Il cache)',
    conjugationAccompli: 'Ffreɣ (J\'ai caché) | Yeffer (Il a caché)',
  },
  'nadi': {
    conjugationInaccompli: 'Naddiyeɣ (Je cherche) | Inaddi (Il cherche)',
    conjugationAccompli: 'Nudaɣ (J\'ai cherché) | Inuda (Il a cherché)',
  },
  'ḥbes': {
    conjugationInaccompli: 'Ḥebbseɣ (J\'arrête) | Iḥebbes (Il arrête)',
    conjugationAccompli: 'Ḥebseɣ (J\'ai arrêté) | Yeḥbes (Il a arrêté)',
  },
  'serreḥ': {
    conjugationInaccompli: 'Serraḥeɣ (Je libère) | Iserraḥ (Il libère)',
    conjugationAccompli: 'Serrḥeɣ (J\'ai libéré) | Iserreḥ (Il a libéré)',
  },
  'jmaɛ': {
    conjugationInaccompli: 'Jemmɛeɣ (Je range) | Ijemmeɛ (Il range)',
    conjugationAccompli: 'Jemɛeɣ (J\'ai rangé) | Yejmeɛ (Il a rangé)',
  },
  'qelleb': {
    conjugationInaccompli: 'Qellibeɣ (Je fouille) | Iqellib (Il fouille)',
    conjugationAccompli: 'Qellbeɣ (J\'ai fouillé) | Iqelleb (Il a fouillé)',
  },
  'zwer': {
    conjugationInaccompli: 'Tezwareɣ (J\'avance) | Itezwar (Il avance)',
    conjugationAccompli: 'Zwareɣ (J\'ai devancé) | Yezwar (Il a devancé)',
  },
  'wexxer': {
    conjugationInaccompli: 'Wexxireɣ (Je recule) | Iwexxir (Il recule)',
    conjugationAccompli: 'Wexxreɣ (J\'ai reculé) | Iwexxer (Il a reculé)',
  },
  'fser': {
    conjugationInaccompli: 'Fessreɣ (J\'étends) | Ifesser (Il étend)',
    conjugationAccompli: 'Fesreɣ (J\'ai étendu) | Yefser (Il a étendu)',
  },
  'sečč': {
    conjugationInaccompli: 'Sseččayeɣ (Je nourris) | Iseččay (Il nourrit)',
    conjugationAccompli: 'Sseččaɣ (J\'ai nourri) | Yessečča (Il a nourri)',
  },
  'tess': {
    conjugationInaccompli: 'Ssettisseɣ (J\'abreuve) | Isettess (Il abreuve)',
    conjugationAccompli: 'Ssesswaɣ (J\'ai abreuvé) | Yessesswa (Il a abreuvé)',
  },
  'sewweq': {
    conjugationInaccompli: 'Sewwayeɣ (Je fais le marché) | Isewway (Il fait le marché)',
    conjugationAccompli: 'Sewwqeɣ (J\'ai fait le marché) | Isewweq (Il a fait le marché)',
  },
  'seɣti': {
    conjugationInaccompli: 'Sseɣtayeɣ (Je corrige) | Iseɣtay (Il corrige)',
    conjugationAccompli: 'Sseɣtaɣ (J\'ai corrigé) | Yesseɣta (Il a corrigé)',
  },
  'qen': {
    conjugationInaccompli: 'Tteqqneɣ (J\'attache) | Iteqqen (Il attache)',
    conjugationAccompli: 'Qqneɣ (J\'ai attaché) | Yeqqen (Il a attaché)',
  },
  'sens': {
    conjugationInaccompli: 'Sennaseɣ (J\'éteins) | Isennas (Il éteint)',
    conjugationAccompli: 'Ssnseɣ (J\'ai éteint) | Yessens (Il a éteint)',
  },
  'ḍes': {
    conjugationInaccompli: 'Teḍsaɣ (Je ris) | Iteḍsa (Il rit)',
    conjugationAccompli: 'Ḍsiɣ (J\'ai ri) | Yeḍsa (Il a ri)',
  },
  'ali': {
    conjugationInaccompli: 'Ttalyeɣ (Je monte) | Ittali (Il monte)',
    conjugationAccompli: 'Uliɣ (Je suis monté) | Yuli (Il est monté)',
  },
  'ṣub': {
    conjugationInaccompli: 'Tteṣubbuɣ (Je descends) | Itteṣubbu (Il descend)',
    conjugationAccompli: 'Ṣubbeɣ (Je suis descendu) | Yeṣubb (Il est descendu)',
  },
  'ɣli': {
    conjugationInaccompli: 'Tteɣlayeɣ (Je tombe) | Itteɣlay (Il tombe)',
    conjugationAccompli: 'Ɣliɣ (Je suis tombé) | Yeɣli (Il est tombé)',
  },
  'neggez': {
    conjugationInaccompli: 'Neggzeɣ (Je saute) | Ineggez (Il saute)',
    conjugationAccompli: 'Neggzeɣ (J\'ai sauté) | Yeneggez (Il a sauté)',
  },
  'qerreb': {
    conjugationInaccompli: 'Qerrbeɣ (Je m\'approche) | Iqerreb (Il s\'approche)',
    conjugationAccompli: 'Qerrbeɣ (Je me suis approché) | Yeqerreb (Il s\'est approché)',
  },
  'beɛɛed': {
    conjugationInaccompli: 'Beɛɛdeɣ (Je m\'éloigne) | Ibeɛɛed (Il s\'éloigne)',
    conjugationAccompli: 'Beɛɛdeɣ (Je me suis éloigné) | Yebeɛɛed (Il s\'est éloigné)',
  },
  'els': {
    conjugationInaccompli: 'Ttluseɣ (Je m\'habille) | Ittlus (Il s\'habille)',
    conjugationAccompli: 'Lsiɣ (Je me suis habillé) | Yels (Il s\'est habillé)',
  },
  'cudd': {
    conjugationInaccompli: 'Ttcudduɣ (J\'attache) | Ittcuddu (Il attache)',
    conjugationAccompli: 'Cuddeɣ (J\'ai attaché) | Yecudd (Il a attaché)',
  },
  'fsi': {
    conjugationInaccompli: 'Ttfesyeɣ (Je dénoue) | Ittfesi (Il dénoue)',
    conjugationAccompli: 'Fsiɣ (J\'ai dénoué) | Yefsi (Il a dénoué)',
  },
  'sfeḍ': {
    conjugationInaccompli: 'Sfeḍeɣ (J\'essuie) | Isfeḍ (Il essuie)',
    conjugationAccompli: 'Sfeḍeɣ (J\'ai essuyé) | Yesfeḍ (Il a essuyé)',
  },
  'ḍegger': {
    conjugationInaccompli: 'Ḍeggireɣ (Je jette) | Iḍeggir (Il jette)',
    conjugationAccompli: 'Ḍeggreɣ (J\'ai jeté) | Yeḍegger (Il a jeté)',
  },
  'wwet': {
    conjugationInaccompli: 'Kkateɣ (Je frappe) | Itekkkat (Il frappe)',
    conjugationAccompli: 'Wwteɣ (J\'ai frappé) | Yewwet (Il a frappé)',
  },
  'qlu': {
    conjugationInaccompli: 'Ttqallayeɣ (Je fais frire) | Ittqallay (Il fait frire)',
    conjugationAccompli: 'Qliɣ (J\'ai fait frire) | Yeqla (Il a fait frire)',
  },
  'eḍs': {
    conjugationInaccompli: 'Teḍsaɣ (Je ris) | Iteḍsa (Il rit)',
    conjugationAccompli: 'Ḍsiɣ (J\'ai ri) | Yeḍsa (Il a ri)',
  },
  'ru': {
    conjugationInaccompli: 'Ttruɣ (Je pleure) | Ittru (Il pleure)',
    conjugationAccompli: 'Rwiɣ (J\'ai pleuré) | Yerwa (Il a pleuré)',
  },
  'ccnu': {
    conjugationInaccompli: 'Ccnawiɣ (Je chante) | Iccnaway (Il chante)',
    conjugationAccompli: 'Cnawen (J\'ai chanté) | Yecna (Il a chanté)',
  },
  'cḍeḥ': {
    conjugationInaccompli: 'Ceḍḍḥeɣ (Je danse) | Iceḍḍeḥ (Il danse)',
    conjugationAccompli: 'Ceḍḥeɣ (J\'ai dansé) | Yeckeḍ (Il a dansé)',
  },
  'zhu': {
    conjugationInaccompli: 'Zzahayeɣ (Je m\'amuse) | Izzahay (Il s\'amuse)',
    conjugationAccompli: 'Zhiɣ (Je me suis amusé) | Yezha (Il s\'est amusé)',
  },
  'steqsi': {
    conjugationInaccompli: 'Steqsayeɣ (Je questionne) | Isteqsay (Il questionne)',
    conjugationAccompli: 'Steqsaɣ (J\'ai demandé) | Yesteqsa (Il a demandé)',
  },
  'jawed': {
    conjugationInaccompli: 'Jawwdeɣ (Je réponds) | Ijawwed (Il répond)',
    conjugationAccompli: 'Jawdeɣ (J\'ai répondu) | Yejawed (Il a répondu)',
  },
  'qbel': {
    conjugationInaccompli: 'Qebbliɣ (J\'accepte) | Iqebbel (Il accepte)',
    conjugationAccompli: 'Qebleɣ (J\'ai accepté) | Yeqbel (Il a accepté)',
  },
  'agi': {
    conjugationInaccompli: 'Ttagiyeɣ (Je refuse) | Ittagi (Il refuse)',
    conjugationAccompli: 'Ugiɣ (J\'ai refusé) | Yugi (Il a refusé)',
  },
  'suter': {
    conjugationInaccompli: 'Suttreɣ (Je sollicite) | Issuttur (Il sollicite)',
    conjugationAccompli: 'Sutreɣ (J\'ai sollicité) | Yessuter (Il a sollicité)',
  },
  'reḍle': {
    conjugationInaccompli: 'Treḍleɣ (Je prête) | Ireḍḍel (Il prête)',
    conjugationAccompli: 'Reḍleɣ (J\'ai prêté) | Yereḍle (Il a prêté)',
  },
  'ssen': {
    conjugationInaccompli: 'Ssneɣ (Je sais) | Issen (Il sait)',
    conjugationAccompli: 'Ssneɣ (J\'ai su) | Yessen (Il a su)',
  },
  'cfu': {
    conjugationInaccompli: 'Ceffayeɣ (Je me souviens) | Iceffay (Il se souvient)',
    conjugationAccompli: 'Cfiɣ (Je me suis souvenu) | Yecfa (Il s\'est souvenu)',
  },
  'ttu': {
    conjugationInaccompli: 'Ttettuɣ (J\'oublie) | Ittettu (Il oublie)',
    conjugationAccompli: 'Ttuɣ (J\'ai oublié) | Yettu (Il a oublié)',
  },

  // --- Nouveaux Mots Fondamentaux ---
  'aseggas': {
    gender: 'm',
    singularKab: 'Aseggas',
    singularFr: 'Année',
    pluralKab: 'Iseggasen',
    pluralFr: 'Années',
  },
  'iseggasen': {
    gender: 'm',
    singularKab: 'Aseggas',
    singularFr: 'Année',
    pluralKab: 'Iseggasen',
    pluralFr: 'Années',
  },
  'aggur': {
    gender: 'm',
    singularKab: 'Aggur',
    singularFr: 'Mois / Lune',
    pluralKab: 'Agguren',
    pluralFr: 'Mois',
  },
  'agguren': {
    gender: 'm',
    singularKab: 'Aggur',
    singularFr: 'Mois / Lune',
    pluralKab: 'Agguren',
    pluralFr: 'Mois',
  },
  'imalas': {
    gender: 'm',
    singularKab: 'Imalas',
    singularFr: 'Semaine',
    pluralKab: 'Imalasen',
    pluralFr: 'Semaines',
  },
  'tusdidt': {
    gender: 'f',
    singularKab: 'Tusdidt',
    singularFr: 'Minute',
    pluralKab: 'Tusdiddin',
    pluralFr: 'Minutes',
  },
  'tafsut': {
    gender: 'f',
    singularKab: 'Tafsut',
    singularFr: 'Printemps',
    pluralKab: 'Tifsiyin',
    pluralFr: 'Printemps',
  },
  'anebdu': {
    gender: 'm',
    singularKab: 'Anebdu',
    singularFr: 'Été',
    pluralKab: 'Inebduyen',
    pluralFr: 'Étés',
  },
  'lexrif': {
    gender: 'm',
    singularKab: 'Lexrif',
    singularFr: 'Automne',
  },
  'tagrest': {
    gender: 'f',
    singularKab: 'Tagrest',
    singularFr: 'Hiver',
    pluralKab: 'Tigrestin',
    pluralFr: 'Hivers',
  },
  'amdan': {
    gender: 'm',
    singularKab: 'Amdan',
    singularFr: 'Être humain / Personne',
    pluralKab: 'Imdanen',
    pluralFr: 'Personnes',
  },
  'imdanen': {
    gender: 'm',
    singularKab: 'Amdan',
    singularFr: 'Être humain / Personne',
    pluralKab: 'Imdanen',
    pluralFr: 'Personnes',
  },
  'akufi': {
    gender: 'm',
    singularKab: 'Akufi',
    singularFr: 'Grande jarre en argile',
    pluralKab: 'Ikufan',
    pluralFr: 'Grandes jarres en argile',
  },
  'ikufan': {
    gender: 'm',
    singularKab: 'Akufi',
    singularFr: 'Grande jarre en argile',
    pluralKab: 'Ikufan',
    pluralFr: 'Grandes jarres en argile',
  },
  'adaynin': {
    gender: 'm',
    singularKab: 'Adaynin',
    singularFr: 'Étable traditionnelle',
    pluralKab: 'Idayninen',
    pluralFr: 'Étables',
  },
  'tasga': {
    gender: 'f',
    singularKab: 'Tasga',
    singularFr: 'Mur d\'honneur',
    pluralKab: 'Tisgiwin',
    pluralFr: 'Murs d\'honneur',
  },
  'ṭṭaq': {
    gender: 'm',
    singularKab: 'Ṭṭaq',
    singularFr: 'Fenêtre',
    pluralKab: 'Ṭṭaqat',
    pluralFr: 'Fenêtres',
  },
  'asqif': {
    gender: 'm',
    singularKab: 'Asqif',
    singularFr: 'Porche d\'entrée',
    pluralKab: 'Iseqfan',
    pluralFr: 'Porches',
  },
  'afarnu': {
    gender: 'm',
    singularKab: 'Afarnu',
    singularFr: 'Four à pain traditionnel',
    pluralKab: 'Ifurna',
    pluralFr: 'Fours à pain',
  },
  'tasekkurt': {
    gender: 'f',
    singularKab: 'Tasekkurt',
    singularFr: 'Perdrix',
    pluralKab: 'Tisekkurin',
    pluralFr: 'Perdrix (pl.)',
  },
  'tisekkurin': {
    gender: 'f',
    singularKab: 'Tasekkurt',
    singularFr: 'Perdrix',
    pluralKab: 'Tisekkurin',
    pluralFr: 'Perdrix (pl.)',
  },
  'izem': {
    gender: 'm',
    singularKab: 'Izem',
    singularFr: 'Lion',
    pluralKab: 'Izmawen',
    pluralFr: 'Lions',
  },
  'izmawen': {
    gender: 'm',
    singularKab: 'Izem',
    singularFr: 'Lion',
    pluralKab: 'Izmawen',
    pluralFr: 'Lions',
  },
  'aserdun': {
    gender: 'm',
    singularKab: 'Aserdun',
    singularFr: 'Mulet',
    pluralKab: 'Iserdyan',
    pluralFr: 'Mulets',
    feminineKab: 'Taserdunt',
  },
  'iserdyan': {
    gender: 'm',
    singularKab: 'Aserdun',
    singularFr: 'Mulet',
    pluralKab: 'Iserdyan',
    pluralFr: 'Mulets',
  },
  'izgaren': {
    gender: 'm',
    singularKab: 'Azger',
    singularFr: 'Bœuf / Taureau',
    pluralKab: 'Izgaren',
    pluralFr: 'Bœufs',
  },
  'aqjun': {
    gender: 'm',
    singularKab: 'Aqjun',
    singularFr: 'Chien / Chiot',
    pluralKab: 'Iqjan',
    pluralFr: 'Chiens',
  },
  'iqjan': {
    gender: 'm',
    singularKab: 'Aqjun',
    singularFr: 'Chien / Chiot',
    pluralKab: 'Iqjan',
    pluralFr: 'Chiens',
  },
  'aɣilas': {
    gender: 'm',
    singularKab: 'Aɣilas',
    singularFr: 'Panthère / Léopard',
    pluralKab: 'Iɣilasen',
    pluralFr: 'Panthères',
  },
  'ibawen': {
    gender: 'm',
    singularKab: 'Ibiw',
    singularFr: 'Fève',
    pluralKab: 'Ibawen',
    pluralFr: 'Fèves',
  },
  'ibiw': {
    gender: 'm',
    singularKab: 'Ibiw',
    singularFr: 'Fève',
    pluralKab: 'Ibawen',
    pluralFr: 'Fèves',
  },
  'taḥlawit': {
    gender: 'f',
    singularKab: 'Taḥlawit',
    singularFr: 'Pâtisserie / Friandise',
    pluralKab: 'Tiḥlawiyin',
    pluralFr: 'Pâtisseries',
  },
  'tiliɣri': {
    gender: 'f',
    singularKab: 'Tiliɣri',
    singularFr: 'Téléphone',
    pluralKab: 'Tiliɣriwin',
    pluralFr: 'Téléphones',
  },
  // --- Le Foyer & La Maison ---
  'axxam': {
    gender: 'm',
    singularKab: 'Axxam',
    singularFr: 'Maison',
    pluralKab: 'Ixxamen',
    pluralFr: 'Maisons',
  },
  'ixxamen': {
    gender: 'm',
    singularKab: 'Axxam',
    singularFr: 'Maison',
    pluralKab: 'Ixxamen',
    pluralFr: 'Maisons',
  },
  'tawwurt': {
    gender: 'f',
    singularKab: 'Tawwurt',
    singularFr: 'Porte',
    pluralKab: 'Tiwwura',
    pluralFr: 'Portes',
  },
  'tiwwura': {
    gender: 'f',
    singularKab: 'Tawwurt',
    singularFr: 'Porte',
    pluralKab: 'Tiwwura',
    pluralFr: 'Portes',
  },
  'tasarut': {
    gender: 'f',
    singularKab: 'Tasarut',
    singularFr: 'Clé',
    pluralKab: 'Tisura',
    pluralFr: 'Clés',
  },
  'tisura': {
    gender: 'f',
    singularKab: 'Tasarut',
    singularFr: 'Clé',
    pluralKab: 'Tisura',
    pluralFr: 'Clés',
  },
  'aɣrab': {
    gender: 'm',
    singularKab: 'Aɣrab',
    singularFr: 'Mur',
    pluralKab: 'Iɣraben',
    pluralFr: 'Murs',
  },
  'iɣraben': {
    gender: 'm',
    singularKab: 'Aɣrab',
    singularFr: 'Mur',
    pluralKab: 'Iɣraben',
    pluralFr: 'Murs',
  },
  'taxxamt': {
    gender: 'f',
    singularKab: 'Taxxamt',
    singularFr: 'Chambre',
    pluralKab: 'Tixxamin',
    pluralFr: 'Chambres',
  },
  'tixxamin': {
    gender: 'f',
    singularKab: 'Taxxamt',
    singularFr: 'Chambre',
    pluralKab: 'Tixxamin',
    pluralFr: 'Chambres',
  },
  'ajgu': {
    gender: 'm',
    singularKab: 'Ajgu',
    singularFr: 'Pilier',
    pluralKab: 'Ijegwan',
    pluralFr: 'Piliers',
  },
  'ssqef': {
    gender: 'm',
    singularKab: 'Ssqef',
    singularFr: 'Toit',
    pluralKab: 'Ssquf',
    pluralFr: 'Toits',
  },
  'lkanun': {
    gender: 'm',
    singularKab: 'Lkanun',
    singularFr: 'Foyer / Cheminée',
    pluralKab: 'Ikanunen',
    pluralFr: 'Foyers',
  },
  'tazeqqa': {
    gender: 'f',
    singularKab: 'Tazeqqa',
    singularFr: 'Grande salle / Pièce commune',
    pluralKab: 'Tizeqwa',
    pluralFr: 'Grandes salles',
  },
  'asalu': {
    gender: 'm',
    singularKab: 'Asalu',
    singularFr: 'Poutre',
    pluralKab: 'Isula',
    pluralFr: 'Poutres',
  },
  'usu': {
    gender: 'm',
    singularKab: 'Usu',
    singularFr: 'Lit',
    pluralKab: 'Uswan',
    pluralFr: 'Lits',
  },
  'tasumta': {
    gender: 'f',
    singularKab: 'Tasumta',
    singularFr: 'Oreiller',
    pluralKab: 'Tisumtiwin',
    pluralFr: 'Oreillers',
  },
  'tasaḍart': {
    gender: 'f',
    singularKab: 'Tasaḍart',
    singularFr: 'Couverture',
    pluralKab: 'Tisaḍarin',
    pluralFr: 'Couvertures',
  },
  'lemri': {
    gender: 'm',
    singularKab: 'Lemri',
    singularFr: 'Miroir',
    pluralKab: 'Lemriyet',
    pluralFr: 'Miroirs',
  },
  'taqerɛet': {
    gender: 'f',
    singularKab: 'Taqerɛet',
    singularFr: 'Bouteille',
    pluralKab: 'Tiqerɛatin',
    pluralFr: 'Bouteilles',
  },
  'aḍebsi': {
    gender: 'm',
    singularKab: 'Aḍebsi',
    singularFr: 'Assiette',
    pluralKab: 'Iḍebsiyen',
    pluralFr: 'Assiettes',
  },
  'taqbuct': {
    gender: 'f',
    singularKab: 'Taqbuct',
    singularFr: 'Tasse',
    pluralKab: 'Tiqbucin',
    pluralFr: 'Tasses',
  },
  'tijɣelt': {
    gender: 'f',
    singularKab: 'Tijɣelt',
    singularFr: 'Cuillère',
    pluralKab: 'Tijɣal',
    pluralFr: 'Cuillères',
  },
  'taferciṭ': {
    gender: 'f',
    singularKab: 'Taferciṭ',
    singularFr: 'Fourchette',
    pluralKab: 'Tiferciḍin',
    pluralFr: 'Fourchettes',
  },
  'ajenwi': {
    gender: 'm',
    singularKab: 'Ajenwi',
    singularFr: 'Couteau',
    pluralKab: 'Ijenwiyen',
    pluralFr: 'Couteaux',
  },

  // --- Animaux & Bêtes ---
  'aydi': {
    gender: 'm',
    singularKab: 'Aydi',
    singularFr: 'Chien',
    pluralKab: 'Iydiwan',
    pluralFr: 'Chiens',
    feminineKab: 'Taydit',
  },
  'taydit': {
    gender: 'f',
    singularKab: 'Taydit',
    singularFr: 'Chienne',
    pluralKab: 'Tiydiyin',
    pluralFr: 'Chiennes',
    masculineKab: 'Aydi',
  },
  'amcic': {
    gender: 'm',
    singularKab: 'Amcic',
    singularFr: 'Chat',
    pluralKab: 'Imcac',
    pluralFr: 'Chats',
    feminineKab: 'Tamcict',
  },
  'tamcict': {
    gender: 'f',
    singularKab: 'Tamcict',
    singularFr: 'Chatte',
    pluralKab: 'Timcicin',
    pluralFr: 'Chattes',
    masculineKab: 'Amcic',
  },
  'ayaziḍ': {
    gender: 'm',
    singularKab: 'Ayaziḍ',
    singularFr: 'Coq',
    pluralKab: 'Iyaziḍen',
    pluralFr: 'Coqs',
    feminineKab: 'Tayaẓiṭ',
  },
  'tayaẓiṭ': {
    gender: 'f',
    singularKab: 'Tayaẓiṭ',
    singularFr: 'Poule',
    pluralKab: 'Tiyaẓiḍin',
    pluralFr: 'Poules',
    masculineKab: 'Ayaziḍ',
  },
  'ayyis': {
    gender: 'm',
    singularKab: 'Ayyis',
    singularFr: 'Cheval',
    pluralKab: 'Iyisan',
    pluralFr: 'Chevaux',
    feminineKab: 'Tagmert',
  },
  'tagmert': {
    gender: 'f',
    singularKab: 'Tagmert',
    singularFr: 'Jument',
    pluralKab: 'Tigmarin',
    pluralFr: 'Juments',
    masculineKab: 'Ayyis',
  },
  'aɣyul': {
    gender: 'm',
    singularKab: 'Aɣyul',
    singularFr: 'Âne',
    pluralKab: 'Iɣyal',
    pluralFr: 'Ânes',
    feminineKab: 'Taɣyult',
  },
  'awtul': {
    gender: 'm',
    singularKab: 'Awtul',
    singularFr: 'Lapin',
    pluralKab: 'Iwtal',
    pluralFr: 'Lapins',
    feminineKab: 'Tawtult',
  },
  'tawtult': {
    gender: 'f',
    singularKab: 'Tawtult',
    singularFr: 'Lapine',
    pluralKab: 'Tiwtalin',
    pluralFr: 'Lapines',
    masculineKab: 'Awtul',
  },
  'ilef': {
    gender: 'm',
    singularKab: 'Ilef',
    singularFr: 'Sanglier',
    pluralKab: 'Ilfan',
    pluralFr: 'Sangliers',
    feminineKab: 'Tileft',
  },
  'tileft': {
    gender: 'f',
    singularKab: 'Tileft',
    singularFr: 'Laie',
    pluralKab: 'Tilfanin',
    pluralFr: 'Laies',
    masculineKab: 'Ilef',
  },
  'tafunast': {
    gender: 'f',
    singularKab: 'Tafunast',
    singularFr: 'Vache',
    pluralKab: 'Tifunasin',
    pluralFr: 'Vaches',
    masculineKab: 'Azger',
  },
  'azger': {
    gender: 'm',
    singularKab: 'Azger',
    singularFr: 'Bœuf / Taureau',
    pluralKab: 'Izgaren',
    pluralFr: 'Bœufs',
    feminineKab: 'Tafunast',
  },
  'ulli': {
    gender: 'f',
    singularKab: 'Tixsi',
    singularFr: 'Brebis',
    pluralKab: 'Ulli',
    pluralFr: 'Brebis / Troupeau',
  },
  'tixsi': {
    gender: 'f',
    singularKab: 'Tixsi',
    singularFr: 'Brebis',
    pluralKab: 'Ulli',
    pluralFr: 'Brebis',
    masculineKab: 'Ikerri',
  },
  'ikerri': {
    gender: 'm',
    singularKab: 'Ikerri',
    singularFr: 'Bélier / Mouton',
    pluralKab: 'Akraren',
    pluralFr: 'Moutons / Béliers',
    feminineKab: 'Tixsi',
  },
  'akraren': {
    gender: 'm',
    singularKab: 'Ikerri',
    singularFr: 'Bélier / Mouton',
    pluralKab: 'Akraren',
    pluralFr: 'Moutons / Béliers',
  },
  'uccen': {
    gender: 'm',
    singularKab: 'Uccen',
    singularFr: 'Chacal / Loup',
    pluralKab: 'Uccanen',
    pluralFr: 'Chacals',
    feminineKab: 'Tuccent',
  },
  'igider': {
    gender: 'm',
    singularKab: 'Igider',
    singularFr: 'Aigle',
    pluralKab: 'Igudar',
    pluralFr: 'Aigles',
  },
  'afrux': {
    gender: 'm',
    singularKab: 'Afrux',
    singularFr: 'Oiseau',
    pluralKab: 'Ifrax',
    pluralFr: 'Oiseaux',
    feminineKab: 'Tafruxt',
  },
  'aslem': {
    gender: 'm',
    singularKab: 'Aslem',
    singularFr: 'Poisson',
    pluralKab: 'Islamen',
    pluralFr: 'Poissons',
  },
  'izi': {
    gender: 'm',
    singularKab: 'Izi',
    singularFr: 'Mouche',
    pluralKab: 'Izan',
    pluralFr: 'Mouches',
  },
  'tzizwit': {
    gender: 'f',
    singularKab: 'Tzizwit',
    singularFr: 'Abeille',
    pluralKab: 'Tizizwa',
    pluralFr: 'Abeilles',
  },
  'aweṭṭuf': {
    gender: 'm',
    singularKab: 'Aweṭṭuf',
    singularFr: 'Fourmi',
    pluralKab: 'Iweṭṭufen',
    pluralFr: 'Fourmis',
    feminineKab: 'Taweṭṭuft',
  },
  'aɣerda': {
    gender: 'm',
    singularKab: 'Aɣerda',
    singularFr: 'Souris',
    pluralKab: 'Iɣerdayen',
    pluralFr: 'Souris',
    feminineKab: 'Taɣerdayt',
  },

  // --- Personnes & Famille ---
  'argaz': {
    gender: 'm',
    singularKab: 'Argaz',
    singularFr: 'Homme',
    pluralKab: 'Irgazen',
    pluralFr: 'Hommes',
    feminineKab: 'Tameṭṭut',
  },
  'irgazen': {
    gender: 'm',
    singularKab: 'Argaz',
    singularFr: 'Homme',
    pluralKab: 'Irgazen',
    pluralFr: 'Hommes',
  },
  'tameṭṭut': {
    gender: 'f',
    singularKab: 'Tameṭṭut',
    singularFr: 'Femme',
    pluralKab: 'Tilawin',
    pluralFr: 'Femmes',
    masculineKab: 'Argaz',
  },
  'tilawin': {
    gender: 'f',
    singularKab: 'Tameṭṭut',
    singularFr: 'Femme',
    pluralKab: 'Tilawin',
    pluralFr: 'Femmes',
  },
  'aqcic': {
    gender: 'm',
    singularKab: 'Aqcic',
    singularFr: 'Garçon',
    pluralKab: 'Iqcicen',
    pluralFr: 'Garçons',
    feminineKab: 'Taqcict',
  },
  'iqcicen': {
    gender: 'm',
    singularKab: 'Aqcic',
    singularFr: 'Garçon',
    pluralKab: 'Iqcicen',
    pluralFr: 'Garçons',
  },
  'taqcict': {
    gender: 'f',
    singularKab: 'Taqcict',
    singularFr: 'Fille',
    pluralKab: 'Tiqcicin',
    pluralFr: 'Filles',
    masculineKab: 'Aqcic',
  },
  'tiqcicin': {
    gender: 'f',
    singularKab: 'Taqcict',
    singularFr: 'Fille',
    pluralKab: 'Tiqcicin',
    pluralFr: 'Filles',
  },
  'amɣar': {
    gender: 'm',
    singularKab: 'Amɣar',
    singularFr: 'Vieil homme / Sage',
    pluralKab: 'Imɣaren',
    pluralFr: 'Vieillards / Sages',
    feminineKab: 'Tamɣart',
  },
  'imɣaren': {
    gender: 'm',
    singularKab: 'Amɣar',
    singularFr: 'Vieil homme',
    pluralKab: 'Imɣaren',
    pluralFr: 'Vieillards',
  },
  'tamɣart': {
    gender: 'f',
    singularKab: 'Tamɣart',
    singularFr: 'Vieille femme',
    pluralKab: 'Timɣarin',
    pluralFr: 'Vieilles femmes',
    masculineKab: 'Amɣar',
  },
  'timɣarin': {
    gender: 'f',
    singularKab: 'Tamɣart',
    singularFr: 'Vieille femme',
    pluralKab: 'Timɣarin',
    pluralFr: 'Vieilles femmes',
  },
  'agellid': {
    gender: 'm',
    singularKab: 'Agellid',
    singularFr: 'Roi',
    pluralKab: 'Igelliden',
    pluralFr: 'Rois',
    feminineKab: 'Tagellidt',
  },
  'igelliden': {
    gender: 'm',
    singularKab: 'Agellid',
    singularFr: 'Roi',
    pluralKab: 'Igelliden',
    pluralFr: 'Rois',
  },
  'tagellidt': {
    gender: 'f',
    singularKab: 'Tagellidt',
    singularFr: 'Reine',
    pluralKab: 'Tigellidin',
    pluralFr: 'Reines',
    masculineKab: 'Agellid',
  },
  'tigellidin': {
    gender: 'f',
    singularKab: 'Tagellidt',
    singularFr: 'Reine',
    pluralKab: 'Tigellidin',
    pluralFr: 'Reines',
  },
  'aselmad': {
    gender: 'm',
    singularKab: 'Aselmad',
    singularFr: 'Professeur',
    pluralKab: 'Iselmaden',
    pluralFr: 'Professeurs',
    feminineKab: 'Taselmadt',
  },
  'iselmaden': {
    gender: 'm',
    singularKab: 'Aselmad',
    singularFr: 'Professeur',
    pluralKab: 'Iselmaden',
    pluralFr: 'Professeurs',
  },
  'taselmadt': {
    gender: 'f',
    singularKab: 'Taselmadt',
    singularFr: 'Professeure',
    pluralKab: 'Tiselmadin',
    pluralFr: 'Professeures',
    masculineKab: 'Aselmad',
  },
  'tiselmadin': {
    gender: 'f',
    singularKab: 'Taselmadt',
    singularFr: 'Professeure',
    pluralKab: 'Tiselmadin',
    pluralFr: 'Professeures',
  },
  'anelmad': {
    gender: 'm',
    singularKab: 'Anelmad',
    singularFr: 'Élève (garçon)',
    pluralKab: 'Inelmaden',
    pluralFr: 'Élèves (garçons)',
    feminineKab: 'Tanelmadt',
  },
  'inelmaden': {
    gender: 'm',
    singularKab: 'Anelmad',
    singularFr: 'Élève',
    pluralKab: 'Inelmaden',
    pluralFr: 'Élèves',
  },
  'tanelmadt': {
    gender: 'f',
    singularKab: 'Tanelmadt',
    singularFr: 'Élève (fille)',
    pluralKab: 'Tinelmadin',
    pluralFr: 'Élèves (filles)',
    masculineKab: 'Anelmad',
  },
  'tinelmadin': {
    gender: 'f',
    singularKab: 'Tanelmadt',
    singularFr: 'Élève',
    pluralKab: 'Tinelmadin',
    pluralFr: 'Élèves',
  },
  'tasaft': {
    gender: 'f',
    singularKab: 'Tasaft',
    singularFr: 'Chêne',
    pluralKab: 'Tisufa',
    pluralFr: 'Chênes',
  },
  'tisufa': {
    gender: 'f',
    singularKab: 'Tasaft',
    singularFr: 'Chêne',
    pluralKab: 'Tisufa',
    pluralFr: 'Chênes',
  },
  'lkar': {
    gender: 'm',
    singularKab: 'Lkar',
    singularFr: 'Bus / Autocar',
    pluralKab: 'Lkarat',
    pluralFr: 'Bus / Autocars',
  },
  'sbiṭar': {
    gender: 'm',
    singularKab: 'Sbiṭar',
    singularFr: 'Hôpital',
    pluralKab: 'Isbiṭaren',
    pluralFr: 'Hôpitaux',
    variants: 'Sbiṭarat',
  },
  'taqsiṭ': {
    gender: 'f',
    singularKab: 'Taqsiṭ',
    singularFr: 'Histoire / Conte',
    pluralKab: 'Tiqsiḍin',
    pluralFr: 'Histoires / Contes',
  },
  'taqṣiṭ': {
    gender: 'f',
    singularKab: 'Taqṣiṭ',
    singularFr: 'Histoire / Conte',
    pluralKab: 'Tiqsiḍin',
    pluralFr: 'Histoires / Contes',
  },
  'tiqsiḍin': {
    gender: 'f',
    singularKab: 'Taqṣiṭ',
    singularFr: 'Histoire / Conte',
    pluralKab: 'Tiqsiḍin',
    pluralFr: 'Histoires / Contes',
  },
  'amezruy': {
    gender: 'm',
    singularKab: 'Amezruy',
    singularFr: 'Histoire',
    pluralKab: 'Imezruyen',
    pluralFr: 'Histoires',
  },
  'amecwar': {
    gender: 'm',
    singularKab: 'Amecwar',
    singularFr: 'Trajet / Distance',
    pluralKab: 'Imecwaren',
    pluralFr: 'Trajets / Distances',
  },
  'tarbaɛt': {
    gender: 'f',
    singularKab: 'Tarbaɛt',
    singularFr: 'Équipe / Groupe',
    pluralKab: 'Tirbaɛin',
    pluralFr: 'Équipes / Groupes',
  },
  'tirbaɛin': {
    gender: 'f',
    singularKab: 'Tarbaɛt',
    singularFr: 'Équipe / Groupe',
    pluralKab: 'Tirbaɛin',
    pluralFr: 'Équipes / Groupes',
  },
  'amur': {
    gender: 'm',
    singularKab: 'Amur',
    singularFr: 'Part / Morceau',
    pluralKab: 'Imuren',
    pluralFr: 'Parts / Morceaux',
  },
  'aḥric': {
    gender: 'm',
    singularKab: 'Aḥric',
    singularFr: 'Partie / Section',
    pluralKab: 'Iḥricen',
    pluralFr: 'Parties / Sections',
  },
  'urar': {
    gender: 'm',
    singularKab: 'Urar',
    singularFr: 'Jeu / Fête',
    pluralKab: 'Uraren',
    pluralFr: 'Jeux / Fêtes',
  },
  'uraren': {
    gender: 'm',
    singularKab: 'Urar',
    singularFr: 'Jeu',
    pluralKab: 'Uraren',
    pluralFr: 'Jeux',
  },
  'tazzla': {
    gender: 'f',
    singularKab: 'Tazzla',
    singularFr: 'Course',
    pluralKab: 'Tizzliwin',
    pluralFr: 'Courses',
  },
  'tiɣrifin': {
    gender: 'f',
    singularKab: 'Taɣriftt',
    singularFr: 'Crêpe traditionnelle (Baghrir)',
    pluralKab: 'Tiɣrifin',
    pluralFr: 'Crêpes traditionnelles',
  },
  'tomatix': {
    gender: 'f',
    singularKab: 'Tomaṭixt',
    singularFr: 'Tomate (une)',
    pluralKab: 'Tomaṭicin',
    pluralFr: 'Tomates',
  },
  'lleft': {
    gender: 'f',
    singularKab: 'Taleffet',
    singularFr: 'Navet (un)',
    pluralKab: 'Lleftat',
    pluralFr: 'Navets',
  },
  'amelluẓ': {
    gender: 'm',
    singularKab: 'Amelluẓ',
    singularFr: 'Affamé',
    pluralKab: 'Imelluẓen',
    pluralFr: 'Affamés',
    feminineKab: 'Tamelluẓt',
  },
  'afaḍan': {
    gender: 'm',
    singularKab: 'Afaḍan',
    singularFr: 'Assoiffé',
    pluralKab: 'Ifaḍanen',
    pluralFr: 'Assoiffés',
    feminineKab: 'Tafaḍant',
  },
  'aɛlayan': {
    gender: 'm',
    singularKab: 'Aɛlayan',
    singularFr: 'Haut / Élevé',
    pluralKab: 'Iɛlayanen',
    pluralFr: 'Hauts / Élevés',
    feminineKab: 'Taɛlayant',
  },
  'aẓayan': {
    gender: 'm',
    singularKab: 'Aẓayan',
    singularFr: 'Lourd',
    pluralKab: 'Iẓayanen',
    pluralFr: 'Lourds',
    feminineKab: 'Taẓayant',
  },
  'afessas': {
    gender: 'm',
    singularKab: 'Afessas',
    singularFr: 'Léger',
    pluralKab: 'Ifessasen',
    pluralFr: 'Légers',
    feminineKab: 'Tafessast',
  },
  'aẓrif': {
    gender: 'm',
    singularKab: 'Aẓrif',
    singularFr: 'Charmant / Mignon',
    pluralKab: 'Iẓrifen',
    pluralFr: 'Charmants / Mignons',
    feminineKab: 'Taẓrift',
  },
  'azaylal': {
    gender: 'm',
    singularKab: 'Azaylal',
    singularFr: 'Rapide',
    pluralKab: 'Izaylalen',
    pluralFr: 'Rapides',
    feminineKab: 'Tazaylalt',
  },
  'aḥerfi': {
    gender: 'm',
    singularKab: 'Aḥerfi',
    singularFr: 'Simple / Modeste',
    pluralKab: 'Iḥerfiyen',
    pluralFr: 'Simples',
    feminineKab: 'Taḥerfit',
  },
  'alemqaq': {
    gender: 'm',
    singularKab: 'Alemqaq',
    singularFr: 'Doux',
    pluralKab: 'Ilemqaqen',
    pluralFr: 'Doux',
    feminineKab: 'Talemqaqt',
  },
  'amidadi': {
    gender: 'm',
    singularKab: 'Amidadi',
    singularFr: 'Bleu',
    pluralKab: 'Imidadiyen',
    pluralFr: 'Bleus',
    feminineKab: 'Tamidadit',
  },
  'aqehwi': {
    gender: 'm',
    singularKab: 'Aqehwi',
    singularFr: 'Marron',
    pluralKab: 'Iqehwiyen',
    pluralFr: 'Marrons',
    feminineKab: 'Taqehwit',
  },
  'ademdam': {
    gender: 'm',
    singularKab: 'Ademdam',
    singularFr: 'Violet foncé',
    pluralKab: 'Idemdamen',
    pluralFr: 'Violets foncés',
    feminineKab: 'Tademdamt',
  },
  'aweṛdi': {
    gender: 'm',
    singularKab: 'Aweṛdi',
    singularFr: 'Rose',
    pluralKab: 'Iweṛdiyen',
    pluralFr: 'Roses',
    feminineKab: 'Taweṛdit',
  },
  'ačini': {
    gender: 'm',
    singularKab: 'Ačini',
    singularFr: 'Orange',
    pluralKab: 'Ičiniyen',
    pluralFr: 'Oranges',
    feminineKab: 'Tačinit',
  },
  'aṛmadi': {
    gender: 'm',
    singularKab: 'Aṛmadi',
    singularFr: 'Gris',
    pluralKab: 'Iṛmadiyen',
    pluralFr: 'Gris',
    feminineKab: 'Taṛmadit',
  },
  'jeddi': {
    gender: 'm',
    singularKab: 'Jeddi',
    singularFr: 'Grand-père',
    pluralKab: 'Jeddiwat',
    pluralFr: 'Grands-pères',
    feminineKab: 'Jida',
  },
  'jida': {
    gender: 'f',
    singularKab: 'Jida',
    singularFr: 'Grand-mère',
    pluralKab: 'Jidawat',
    pluralFr: 'Grands-mères',
    masculineKab: 'Jeddi',
  },
  'mmi': {
    gender: 'm',
    singularKab: 'Mmi',
    singularFr: 'Mon fils',
    pluralKab: 'Arraw-iw',
    pluralFr: 'Mes fils / enfants',
    feminineKab: 'Yelli',
  },
  'yelli': {
    gender: 'f',
    singularKab: 'Yelli',
    singularFr: 'Ma fille',
    pluralKab: 'Yesset-iw',
    pluralFr: 'Mes filles',
    masculineKab: 'Mmi',
  },
  'a gma': {
    gender: 'm',
    singularKab: 'Gma',
    singularFr: 'Mon frère',
    pluralKab: 'Atmaten-iw',
    pluralFr: 'Mes frères',
    feminineKab: 'A weltma',
  },
  'a weltma': {
    gender: 'f',
    singularKab: 'Weltma',
    singularFr: 'Ma sœur',
    pluralKab: 'Tissetin-iw',
    pluralFr: 'Mes sœurs',
    masculineKab: 'A gma',
  },
  'xali': {
    gender: 'm',
    singularKab: 'Xali',
    singularFr: 'Mon oncle maternel',
    pluralKab: 'Xawali',
    pluralFr: 'Mes oncles maternels',
    feminineKab: 'Xalti',
  },
  'xalti': {
    gender: 'f',
    singularKab: 'Xalti',
    singularFr: 'Ma tante maternelle',
    pluralKab: 'Xalati',
    pluralFr: 'Mes tantes maternelles',
    masculineKab: 'Xali',
  },
  'ɛemmi': {
    gender: 'm',
    singularKab: 'Ɛemmi',
    singularFr: 'Mon oncle paternel',
    pluralKab: 'Leɛmum',
    pluralFr: 'Mes oncles paternels',
    feminineKab: 'Ɛemmti',
  },
  'ɛemmti': {
    gender: 'f',
    singularKab: 'Ɛemmti',
    singularFr: 'Ma tante paternelle',
    pluralKab: 'Tiɛemmatin',
    pluralFr: 'Mes tantes paternelles',
    masculineKab: 'Ɛemmi',
  },
  'baba': {
    gender: 'm',
    singularKab: 'Baba',
    singularFr: 'Mon père',
    pluralKab: 'Ibabaten',
    pluralFr: 'Pères',
    feminineKab: 'Yemma',
  },
  'yemma': {
    gender: 'f',
    singularKab: 'Yemma',
    singularFr: 'Ma mère',
    pluralKab: 'Iyemmaten',
    pluralFr: 'Mères',
    masculineKab: 'Baba',
  },
  'ayaw': {
    gender: 'm',
    singularKab: 'Ayaw',
    singularFr: 'Neveu / Petit-fils',
    pluralKab: 'Iyawen',
    pluralFr: 'Neveux / Petits-fils',
    feminineKab: 'Tayawt',
  },
  'tayawt': {
    gender: 'f',
    singularKab: 'Tayawt',
    singularFr: 'Nièce / Petite-fille',
    pluralKab: 'Tiyawin',
    pluralFr: 'Nièces / Petites-filles',
    masculineKab: 'Ayaw',
  },
  'aḍeggal': {
    gender: 'm',
    singularKab: 'Aḍeggal',
    singularFr: 'Beau-père / Beau-frère',
    pluralKab: 'Iḍeggwalen',
    pluralFr: 'Beaux-pères / Beaux-frères',
  },
  'taɣaṭ': {
    gender: 'f',
    singularKab: 'Taɣaṭ',
    singularFr: 'Chèvre',
    pluralKab: 'Tiɣaṭen',
    pluralFr: 'Chèvres',
  },
  'izimer': {
    gender: 'm',
    singularKab: 'Izimer',
    singularFr: 'Agneau',
    pluralKab: 'Izamaren',
    pluralFr: 'Agneaux',
  },
  'ibki': {
    gender: 'm',
    singularKab: 'Ibki',
    singularFr: 'Singe',
    pluralKab: 'Ibkawen',
    pluralFr: 'Singes',
  },
  'azrem': {
    gender: 'm',
    singularKab: 'Azrem',
    singularFr: 'Serpent',
    pluralKab: 'Izerman',
    pluralFr: 'Serpents',
  },
  'tawwukt': {
    gender: 'f',
    singularKab: 'Tawwukt',
    singularFr: 'Chouette / Hibou',
    pluralKab: 'Tiwwukin',
    pluralFr: 'Chouettes / Hiboux',
  },
  'tifirest': {
    gender: 'f',
    singularKab: 'Tifirest',
    singularFr: 'Poire',
    pluralKab: 'Tifiras',
    pluralFr: 'Poires',
  },
  'tavelut': {
    gender: 'f',
    singularKab: 'Tavelut',
    singularFr: 'Vélo / Bicyclette',
    pluralKab: 'Tivelutin',
    pluralFr: 'Vélos',
  },
  'aɣawec': {
    gender: 'm',
    singularKab: 'Aɣawec',
    singularFr: 'Bébé',
    pluralKab: 'Iɣawcen',
    pluralFr: 'Bébés',
  },

  // --- Nourriture & Boissons (Modernes & Usuels) ---
  'lifrit': {
    gender: 'f',
    singularKab: 'Lifrit',
    singularFr: 'Frite',
    pluralKab: 'Lifrit',
    pluralFr: 'Frites',
  },
  'lkanet': {
    gender: 'f',
    singularKab: 'Lkanet',
    singularFr: 'Canette',
    pluralKab: 'Lkanetat',
    pluralFr: 'Canettes',
  },
  'gazuz': {
    gender: 'm',
    singularKab: 'Gazuz',
    singularFr: 'Soda / Gazouz',
  },
  'sanwicc': {
    gender: 'm',
    singularKab: 'Sanwicc',
    singularFr: 'Sandwich',
    pluralKab: 'Isanwiccen',
    pluralFr: 'Sandwichs',
    variants: 'Lkasrut',
  },
  'lkasrut': {
    gender: 'm',
    singularKab: 'Lkasrut',
    singularFr: 'Casse-croûte / Sandwich',
    pluralKab: 'Lkasrutat',
    pluralFr: 'Casse-croûtes',
  },
  'pitsat': {
    gender: 'f',
    singularKab: 'Pitsat',
    singularFr: 'Pizza',
    pluralKab: 'Pitsawat',
    pluralFr: 'Pizzas',
  },
  'aglis': {
    gender: 'm',
    singularKab: 'Aglis',
    singularFr: 'Glace / Cornet',
    pluralKab: 'Iglisen',
    pluralFr: 'Glaces',
  },
  'jji': {
    gender: 'm',
    singularKab: 'Jji',
    singularFr: 'Jus de fruit',
    pluralKab: 'Jjiyat',
    pluralFr: 'Jus',
  },
  'afermaj': {
    gender: 'm',
    singularKab: 'Afermaj',
    singularFr: 'Fromage',
    pluralKab: 'Ifermajen',
    pluralFr: 'Fromages',
  },
  'aferruj': {
    gender: 'm',
    singularKab: 'Aferruj',
    singularFr: 'Poulet / Coquelet',
    pluralKab: 'Iferrujen',
    pluralFr: 'Poulets',
  },
  'claḍa': {
    gender: 'f',
    singularKab: 'Claḍa',
    singularFr: 'Salade',
    pluralKab: 'Claḍat',
    pluralFr: 'Salades',
  },
  'zzrudiyya': {
    gender: 'f',
    singularKab: 'Zzrudiyya',
    singularFr: 'Carotte',
    pluralKab: 'Zzrudiyyat',
    pluralFr: 'Carottes',
  },
  'ṭumaṭic': {
    gender: 'f',
    singularKab: 'Ṭumaṭic',
    singularFr: 'Tomate',
    pluralKab: 'Ṭumaṭicin',
    pluralFr: 'Tomates',
  },
  'lbaṭaṭa': {
    gender: 'f',
    singularKab: 'Lbaṭaṭa',
    singularFr: 'Pomme de terre',
    pluralKab: 'Lbaṭaṭat',
    pluralFr: 'Pommes de terre',
  },
  'tameqfult': {
    gender: 'f',
    singularKab: 'Tameqfult',
    singularFr: 'Couscous aux légumes vapeur',
  },
  'lesfenǧ': {
    gender: 'm',
    singularKab: 'Tasfenǧet',
    singularFr: 'Beignet',
    pluralKab: 'Lesfenǧ',
    pluralFr: 'Beignets',
  },
  'ccerba': {
    gender: 'f',
    singularKab: 'Ccerba',
    singularFr: 'Chorba / Soupe',
    pluralKab: 'Ccerbat',
    pluralFr: 'Soupes',
  },
  'iyi': {
    gender: 'm',
    singularKab: 'Iyi',
    singularFr: 'Lait fermenté (lben)',
  },
  'ḍebsi': {
    gender: 'm',
    singularKab: 'Ḍebsi',
    singularFr: 'Assiette / Plat',
    pluralKab: 'Iḍebsiyen',
    pluralFr: 'Assiettes',
  },
  'nneɛnaɛ': {
    gender: 'm',
    singularKab: 'Nneɛnaɛ',
    singularFr: 'Menthe',
  },
  'meɛdnus': {
    gender: 'm',
    singularKab: 'Meɛdnus',
    singularFr: 'Persil',
  },
  'lkesbur': {
    gender: 'm',
    singularKab: 'Lkesbur',
    singularFr: 'Coriandre',
  },
  'zzɛter': {
    gender: 'm',
    singularKab: 'Zzɛter',
    singularFr: 'Thym / Origan',
  },
  'leḥbaq': {
    gender: 'm',
    singularKab: 'Leḥbaq',
    singularFr: 'Basilic',
  },
  'felgu': {
    gender: 'm',
    singularKab: 'Felgu',
    singularFr: 'Menthe sauvage (pouliot)',
  },
  'lkemmun': {
    gender: 'm',
    singularKab: 'Lkemmun',
    singularFr: 'Cumin',
  },
  'lqeṛfa': {
    gender: 'f',
    singularKab: 'Lqeṛfa',
    singularFr: 'Cannelle',
  },
  'lkesba': {
    gender: 'f',
    singularKab: 'Lkesba',
    singularFr: 'Feuille de laurier',
    pluralKab: 'Lkesbat',
    pluralFr: 'Feuilles de laurier',
  },

  // --- Le Corps & Santé ---
  'aqerruy': {
    gender: 'm',
    singularKab: 'Aqerruy',
    singularFr: 'Tête',
    pluralKab: 'Iqerray',
    pluralFr: 'Têtes',
  },
  'afus': {
    gender: 'm',
    singularKab: 'Afus',
    singularFr: 'Main',
    pluralKab: 'Ifassen',
    pluralFr: 'Mains',
  },
  'ifassen': {
    gender: 'm',
    singularKab: 'Afus',
    singularFr: 'Main',
    pluralKab: 'Ifassen',
    pluralFr: 'Mains',
  },
  'aḍar': {
    gender: 'm',
    singularKab: 'Aḍar',
    singularFr: 'Pied',
    pluralKab: 'Iḍarren',
    pluralFr: 'Pieds',
  },
  'iḍarren': {
    gender: 'm',
    singularKab: 'Aḍar',
    singularFr: 'Pied',
    pluralKab: 'Iḍarren',
    pluralFr: 'Pieds',
  },
  'allen': {
    gender: 'f',
    singularKab: 'Tiṭ',
    singularFr: 'Œil',
    pluralKab: 'Allen',
    pluralFr: 'Yeux',
  },
  'tiṭ': {
    gender: 'f',
    singularKab: 'Tiṭ',
    singularFr: 'Œil',
    pluralKab: 'Allen',
    pluralFr: 'Yeux',
  },
  'imi': {
    gender: 'm',
    singularKab: 'Imi',
    singularFr: 'Bouche',
    pluralKab: 'Imawen',
    pluralFr: 'Bouches',
  },
  'iles': {
    gender: 'm',
    singularKab: 'Iles',
    singularFr: 'Langue',
    pluralKab: 'Ilsawen',
    pluralFr: 'Langues',
  },
  'ameẓẓuɣ': {
    gender: 'm',
    singularKab: 'Ameẓẓuɣ',
    singularFr: 'Oreille',
    pluralKab: 'Imeẓẓuɣen',
    pluralFr: 'Oreilles',
  },
  'imeẓẓuɣen': {
    gender: 'm',
    singularKab: 'Ameẓẓuɣ',
    singularFr: 'Oreille',
    pluralKab: 'Imeẓẓuɣen',
    pluralFr: 'Oreilles',
  },
  'tuɣmest': {
    gender: 'f',
    singularKab: 'Tuɣmest',
    singularFr: 'Dent',
    pluralKab: 'Tuɣmas',
    pluralFr: 'Dents',
  },
  'tuɣmas': {
    gender: 'f',
    singularKab: 'Tuɣmest',
    singularFr: 'Dent',
    pluralKab: 'Tuɣmas',
    pluralFr: 'Dents',
  },
  'tifdent': {
    gender: 'f',
    singularKab: 'Tifdent',
    singularFr: 'Orteil',
    pluralKab: 'Tifdiniwin',
    pluralFr: 'Orteils',
  },
  'tifdiniwin': {
    gender: 'f',
    singularKab: 'Tifdent',
    singularFr: 'Orteil',
    pluralKab: 'Tifdiniwin',
    pluralFr: 'Orteils',
  },
  'tanzaren': {
    gender: 'f',
    singularKab: 'Tanzert',
    singularFr: 'Narine',
    pluralKab: 'Tanzaren',
    pluralFr: 'Nez / Narines',
  },
  'tanzert': {
    gender: 'f',
    singularKab: 'Tanzert',
    singularFr: 'Narine',
    pluralKab: 'Tanzaren',
    pluralFr: 'Nez / Narines',
  },
  'idmaren': {
    gender: 'm',
    singularKab: 'Idmaren',
    singularFr: 'Poitrine / Pectoraux (pl.)',
    pluralKab: 'Idmaren',
    pluralFr: 'Poitrine / Pectoraux',
  },
  'ul': {
    gender: 'm',
    singularKab: 'Ul',
    singularFr: 'Cœur',
    pluralKab: 'Ulawen',
    pluralFr: 'Cœurs',
  },
  'iɣil': {
    gender: 'm',
    singularKab: 'Iɣil',
    singularFr: 'Bras / Colline',
    pluralKab: 'Iɣallen',
    pluralFr: 'Bras / Collines',
  },
  'aɛrur': {
    gender: 'm',
    singularKab: 'Aɛrur',
    singularFr: 'Dos',
    pluralKab: 'Iɛruren',
    pluralFr: 'Dos',
  },
  'tamart': {
    gender: 'f',
    singularKab: 'Tamart',
    singularFr: 'Barbe',
    pluralKab: 'Timira',
    pluralFr: 'Barbes',
  },
  'anyir': {
    gender: 'm',
    singularKab: 'Anyir',
    singularFr: 'Front',
    pluralKab: 'Inyiren',
    pluralFr: 'Fronts',
  },
  'agusim': {
    gender: 'm',
    singularKab: 'Agusim',
    singularFr: 'Joue',
    pluralKab: 'Igusimen',
    pluralFr: 'Joues',
  },
  'tawaẓẓa': {
    gender: 'f',
    singularKab: 'Tawaẓẓa',
    singularFr: 'Rhume / Grippe',
    pluralKab: 'Tiwaẓẓiwin',
    pluralFr: 'Rhumes',
  },
  'azeṭṭad': {
    gender: 'm',
    singularKab: 'Azeṭṭad',
    singularFr: 'Rhume / Écoulement nasal',
    pluralKab: 'Izeṭṭaden',
    pluralFr: 'Rhumes',
  },
  'tusut': {
    gender: 'f',
    singularKab: 'Tusut',
    singularFr: 'Toux',
    pluralKab: 'Tusutin',
    pluralFr: 'Toux (pl.)',
  },
  'tawla': {
    gender: 'f',
    singularKab: 'Tawla',
    singularFr: 'Fièvre',
    pluralKab: 'Tawliwin',
    pluralFr: 'Fièvres',
  },
  'aqraḥ': {
    gender: 'm',
    singularKab: 'Aqraḥ',
    singularFr: 'Douleur / Mal',
    pluralKab: 'Iqraḥen',
    pluralFr: 'Douleurs / Maux',
  },
  'aṭṭan': {
    gender: 'm',
    singularKab: 'Aṭṭan',
    singularFr: 'Maladie',
    pluralKab: 'Aṭṭanen',
    pluralFr: 'Maladies',
  },
  'amuḍin': {
    gender: 'm',
    singularKab: 'Amuḍin',
    singularFr: 'Malade',
    pluralKab: 'Imuḍinen',
    pluralFr: 'Malades',
    feminineKab: 'Tamuḍint',
  },
  'tamuḍint': {
    gender: 'f',
    singularKab: 'Tamuḍint',
    singularFr: 'Malade (f.)',
    pluralKab: 'Timuḍinin',
    pluralFr: 'Malades (f.)',
    masculineKab: 'Amuḍin',
  },
  'ljerḥ': {
    gender: 'm',
    singularKab: 'Ljerḥ',
    singularFr: 'Blessure / Plaie',
    pluralKab: 'Lejruḥ',
    pluralFr: 'Blessures / Plaies',
  },
  'ajerraḥ': {
    gender: 'm',
    singularKab: 'Ajerraḥ',
    singularFr: 'Blessure / Coupure',
    pluralKab: 'Ijerraḥen',
    pluralFr: 'Blessures',
  },
  'idamen': {
    gender: 'm',
    singularKab: 'Idamen',
    singularFr: 'Sang (pl.)',
    pluralKab: 'Idamen',
    pluralFr: 'Sang',
  },
  'ddwa': {
    gender: 'm',
    singularKab: 'Ddwa',
    singularFr: 'Médicament / Remède',
    pluralKab: 'Ddwawat',
    pluralFr: 'Médicaments',
  },
  'asafar': {
    gender: 'm',
    singularKab: 'Asafar',
    singularFr: 'Remède / Médicament',
    pluralKab: 'Isafaren',
    pluralFr: 'Remèdes',
  },
  'ṭbib': {
    gender: 'm',
    singularKab: 'Ṭbib',
    singularFr: 'Médecin / Docteur',
    pluralKab: 'Iṭbiben',
    pluralFr: 'Médecins',
    feminineKab: 'Taṭbibt',
  },
  'taṭbibt': {
    gender: 'f',
    singularKab: 'Taṭbibt',
    singularFr: 'Médecin (femme)',
    pluralKab: 'Tiṭbibin',
    pluralFr: 'Médecins (femmes)',
    masculineKab: 'Ṭbib',
  },
  'imejji': {
    gender: 'm',
    singularKab: 'Imejji',
    singularFr: 'Soignant / Médecin',
    pluralKab: 'Imejjiyen',
    pluralFr: 'Médecins / Soignants',
  },
  'asegnaf': {
    gender: 'm',
    singularKab: 'Asegnaf',
    singularFr: 'Hôpital / Clinique',
    pluralKab: 'Isegnafen',
    pluralFr: 'Hôpitaux',
  },
  'lfarmasi': {
    gender: 'f',
    singularKab: 'Lfarmasi',
    singularFr: 'Pharmacie',
    pluralKab: 'Lfarmasiyat',
    pluralFr: 'Pharmacies',
  },
  'tasafert': {
    gender: 'f',
    singularKab: 'Tasafert',
    singularFr: 'Officine / Pharmacie',
    pluralKab: 'Tisaferin',
    pluralFr: 'Pharmacies',
  },
  'tacdadt': {
    gender: 'f',
    singularKab: 'Tacdadt',
    singularFr: 'Pansement / Bandage',
    pluralKab: 'Ticdadin',
    pluralFr: 'Pansements / Bandages',
  },
  'lfasma': {
    gender: 'f',
    singularKab: 'Lfasma',
    singularFr: 'Compresse / Pansement',
    pluralKab: 'Lfasmat',
    pluralFr: 'Compresses',
  },
  'tigezzt': {
    gender: 'f',
    singularKab: 'Tigezzt',
    singularFr: 'Piqûre / Vaccin',
    pluralKab: 'Tigezzin',
    pluralFr: 'Piqûres / Vaccins',
  },
  'tazmert': {
    gender: 'f',
    singularKab: 'Tazmert',
    singularFr: 'Santé / Vigueur',
    pluralKab: 'Tizmert',
    pluralFr: 'Capacités / Forces',
  },
  'ccfa': {
    gender: 'm',
    singularKab: 'Ccfa',
    singularFr: 'Guérison / Rétablissement',
  },
  'aɛḍas': {
    gender: 'm',
    singularKab: 'Aɛḍas',
    singularFr: 'Éternuement',
    pluralKab: 'Iɛḍasen',
    pluralFr: 'Éternuements',
  },
  'aɛebbuḍ': {
    gender: 'm',
    singularKab: 'Aɛebbuḍ',
    singularFr: 'Ventre / Estomac',
    pluralKab: 'Iɛebbaḍ',
    pluralFr: 'Ventres',
  },
  'aglim': {
    gender: 'm',
    singularKab: 'Aglim',
    singularFr: 'Peau',
    pluralKab: 'Iglimen',
    pluralFr: 'Peaux',
  },
  'iɣes': {
    gender: 'm',
    singularKab: 'Iɣes',
    singularFr: 'Os',
    pluralKab: 'Iɣsan',
    pluralFr: 'Os (pl.)',
  },
  'anẓad': {
    gender: 'm',
    singularKab: 'Anẓad',
    singularFr: 'Cheveu / Poil',
    pluralKab: 'Anẓaden',
    pluralFr: 'Cheveux',
  },
  'anẓaden': {
    gender: 'm',
    singularKab: 'Anẓad',
    singularFr: 'Cheveu / Poil',
    pluralKab: 'Anẓaden',
    pluralFr: 'Cheveux',
  },
  'iccer': {
    gender: 'm',
    singularKab: 'Iccer',
    singularFr: 'Ongle',
    pluralKab: 'Iccaren',
    pluralFr: 'Ongles',
  },
  'iccaren': {
    gender: 'm',
    singularKab: 'Iccer',
    singularFr: 'Ongle',
    pluralKab: 'Iccaren',
    pluralFr: 'Ongles',
  },
  'afud': {
    gender: 'm',
    singularKab: 'Afud',
    singularFr: 'Genou / Force des jambes',
    pluralKab: 'Ifadden',
    pluralFr: 'Genoux',
  },
  'ifadden': {
    gender: 'm',
    singularKab: 'Afud',
    singularFr: 'Genou',
    pluralKab: 'Ifadden',
    pluralFr: 'Genoux',
  },
  'taɣerɣert': {
    gender: 'f',
    singularKab: 'Taɣerɣert',
    singularFr: 'Gorge / Larynx',
    pluralKab: 'Tiɣerɣar',
    pluralFr: 'Gorges',
  },
  'tafekka': {
    gender: 'f',
    singularKab: 'Tafekka',
    singularFr: 'Corps (physique)',
    pluralKab: 'Tifekkiwin',
    pluralFr: 'Corps (pl.)',
  },
  'lǧetta': {
    gender: 'f',
    singularKab: 'Lǧetta',
    singularFr: 'Corps (humain)',
    pluralKab: 'Leǧtat',
    pluralFr: 'Corps (pl.)',
  },
  'iḍeḍ': {
    gender: 'm',
    singularKab: 'Iḍeḍ',
    singularFr: 'Doigt',
    pluralKab: 'Iḍudan',
    pluralFr: 'Doigts',
  },
  'iḍudan': {
    gender: 'm',
    singularKab: 'Iḍeḍ',
    singularFr: 'Doigt',
    pluralKab: 'Iḍudan',
    pluralFr: 'Doigts',
  },
  'tamgert': {
    gender: 'f',
    singularKab: 'Tamgert',
    singularFr: 'Cou / Nuque',
    pluralKab: 'Timgad',
    pluralFr: 'Cous',
  },
  'turin': {
    gender: 'f',
    singularKab: 'Tureft',
    singularFr: 'Poumon',
    pluralKab: 'Turin',
    pluralFr: 'Poumons',
  },
  'tasa': {
    gender: 'f',
    singularKab: 'Tasa',
    singularFr: 'Foie / Affection profonde',
    pluralKab: 'Tasiwin',
    pluralFr: 'Foies',
  },
  'udem': {
    gender: 'm',
    singularKab: 'Udem',
    singularFr: 'Visage',
    pluralKab: 'Udmawen',
    pluralFr: 'Visages',
  },

  // --- Vêtements ---
  'aserwal': {
    gender: 'm',
    singularKab: 'Aserwal',
    singularFr: 'Pantalon',
    pluralKab: 'Iserwalen',
    pluralFr: 'Pantalons',
  },
  'iserwalen': {
    gender: 'm',
    singularKab: 'Aserwal',
    singularFr: 'Pantalon',
    pluralKab: 'Iserwalen',
    pluralFr: 'Pantalons',
  },
  'aqendur': {
    gender: 'm',
    singularKab: 'Aqendur',
    singularFr: 'Robe kabyle',
    pluralKab: 'Iqenduren',
    pluralFr: 'Robes',
  },
  'iqenduren': {
    gender: 'm',
    singularKab: 'Aqendur',
    singularFr: 'Robe',
    pluralKab: 'Iqenduren',
    pluralFr: 'Robes',
  },
  'isebbaden': {
    gender: 'm',
    singularKab: 'Asebbad',
    singularFr: 'Chaussure',
    pluralKab: 'Isebbaden',
    pluralFr: 'Chaussures',
  },
  'asebbad': {
    gender: 'm',
    singularKab: 'Asebbad',
    singularFr: 'Chaussure',
    pluralKab: 'Isebbaden',
    pluralFr: 'Chaussures',
  },
  'taqemǧet': {
    gender: 'f',
    singularKab: 'Taqemǧet',
    singularFr: 'Chemise',
    pluralKab: 'Tiqemǧatin',
    pluralFr: 'Chemises',
  },
  'tiqemǧatin': {
    gender: 'f',
    singularKab: 'Taqemǧet',
    singularFr: 'Chemise',
    pluralKab: 'Tiqemǧatin',
    pluralFr: 'Chemises',
  },
  'tqecririn': {
    gender: 'f',
    singularKab: 'Taqecrirt',
    singularFr: 'Chaussette',
    pluralKab: 'Tqecririn',
    pluralFr: 'Chaussettes',
  },
  'taqecrirt': {
    gender: 'f',
    singularKab: 'Taqecrirt',
    singularFr: 'Chaussette',
    pluralKab: 'Tqecririn',
    pluralFr: 'Chaussettes',
  },

  // --- Nature & Géographie ---
  'taddart': {
    gender: 'f',
    singularKab: 'Taddart',
    singularFr: 'Village',
    pluralKab: 'Tuddar',
    pluralFr: 'Villages',
  },
  'tuddar': {
    gender: 'f',
    singularKab: 'Taddart',
    singularFr: 'Village',
    pluralKab: 'Tuddar',
    pluralFr: 'Villages',
  },
  'tamurt': {
    gender: 'f',
    singularKab: 'Tamurt',
    singularFr: 'Pays / Terre',
    pluralKab: 'Timura',
    pluralFr: 'Pays',
  },
  'timura': {
    gender: 'f',
    singularKab: 'Tamurt',
    singularFr: 'Pays',
    pluralKab: 'Timura',
    pluralFr: 'Pays',
  },
  'asif': {
    gender: 'm',
    singularKab: 'Asif',
    singularFr: 'Rivière',
    pluralKab: 'Isaffen',
    pluralFr: 'Rivières',
  },
  'isaffen': {
    gender: 'm',
    singularKab: 'Asif',
    singularFr: 'Rivière',
    pluralKab: 'Isaffen',
    pluralFr: 'Rivières',
  },
  'abrid': {
    gender: 'm',
    singularKab: 'Abrid',
    singularFr: 'Chemin',
    pluralKab: 'Iberdan',
    pluralFr: 'Chemins',
  },
  'iberdan': {
    gender: 'm',
    singularKab: 'Abrid',
    singularFr: 'Chemin',
    pluralKab: 'Iberdan',
    pluralFr: 'Chemins',
  },
  'adrar': {
    gender: 'm',
    singularKab: 'Adrar',
    singularFr: 'Montagne',
    pluralKab: 'Idurar',
    pluralFr: 'Montagnes',
  },
  'idurar': {
    gender: 'm',
    singularKab: 'Adrar',
    singularFr: 'Montagne',
    pluralKab: 'Idurar',
    pluralFr: 'Montagnes',
  },
  'tala': {
    gender: 'f',
    singularKab: 'Tala',
    singularFr: 'Fontaine / Source',
    pluralKab: 'Tiliwa',
    pluralFr: 'Fontaines / Sources',
  },
  'tiliwa': {
    gender: 'f',
    singularKab: 'Tala',
    singularFr: 'Fontaine',
    pluralKab: 'Tiliwa',
    pluralFr: 'Fontaines',
  },
  'ttejra': {
    gender: 'f',
    singularKab: 'Ttejra',
    singularFr: 'Arbre',
    pluralKab: 'Ttejrat',
    pluralFr: 'Arbres',
  },
  'tacḍaṭ': {
    gender: 'f',
    singularKab: 'Tacḍaṭ',
    singularFr: 'Branche',
    pluralKab: 'Ticḍaḍin',
    pluralFr: 'Branches',
  },
  'ajeǧǧig': {
    gender: 'm',
    singularKab: 'Ajeǧǧig',
    singularFr: 'Fleur',
    pluralKab: 'Ijeǧǧigen',
    pluralFr: 'Fleurs',
  },
  'ijeǧǧigen': {
    gender: 'm',
    singularKab: 'Ajeǧǧig',
    singularFr: 'Fleur',
    pluralKab: 'Ijeǧǧigen',
    pluralFr: 'Fleurs',
  },
  'amadaɣ': {
    gender: 'm',
    singularKab: 'Amadaɣ',
    singularFr: 'Forêt',
    pluralKab: 'Imudaɣ',
    pluralFr: 'Forêts',
  },
  'asignew': {
    gender: 'm',
    singularKab: 'Asignew',
    singularFr: 'Nuage',
    pluralKab: 'Isignawen',
    pluralFr: 'Nuages',
  },
  'isignawen': {
    gender: 'm',
    singularKab: 'Asignew',
    singularFr: 'Nuage',
    pluralKab: 'Isignawen',
    pluralFr: 'Nuages',
  },
  'itri': {
    gender: 'm',
    singularKab: 'Itri',
    singularFr: 'Étoile',
    pluralKab: 'Itran',
    pluralFr: 'Étoiles',
  },
  'itran': {
    gender: 'm',
    singularKab: 'Itri',
    singularFr: 'Étoile',
    pluralKab: 'Itran',
    pluralFr: 'Étoiles',
  },
  'ablaḍ': {
    gender: 'm',
    singularKab: 'Ablaḍ',
    singularFr: 'Caillou / Pierre',
    pluralKab: 'Iblaḍen',
    pluralFr: 'Cailloux / Pierres',
  },
  'iblaḍen': {
    gender: 'm',
    singularKab: 'Ablaḍ',
    singularFr: 'Caillou',
    pluralKab: 'Iblaḍen',
    pluralFr: 'Cailloux',
  },
  'aḍu': {
    gender: 'm',
    singularKab: 'Aḍu',
    singularFr: 'Vent',
    pluralKab: 'Iḍuḍan',
    pluralFr: 'Vents',
  },
  'tafat': {
    gender: 'f',
    singularKab: 'Tafat',
    singularFr: 'Lumière',
    pluralKab: 'Tifawin',
    pluralFr: 'Lumières',
  },
  'aman': {
    gender: 'm',
    singularKab: 'Aman',
    singularFr: 'Eau (toujours pl. en kabyle)',
    pluralKab: 'Aman',
    pluralFr: 'Eau',
  },

  // --- École, Culture & Savoir ---
  'adlis': {
    gender: 'm',
    singularKab: 'Adlis',
    singularFr: 'Livre',
    pluralKab: 'Idlisen',
    pluralFr: 'Livres',
  },
  'idlisen': {
    gender: 'm',
    singularKab: 'Adlis',
    singularFr: 'Livre',
    pluralKab: 'Idlisen',
    pluralFr: 'Livres',
  },
  'tafeṭṭart': {
    gender: 'f',
    singularKab: 'Tafeṭṭart',
    singularFr: 'Cahier',
    pluralKab: 'Tifeṭṭarin',
    pluralFr: 'Cahiers',
  },
  'tifeṭṭarin': {
    gender: 'f',
    singularKab: 'Tafeṭṭart',
    singularFr: 'Cahier',
    pluralKab: 'Tifeṭṭarin',
    pluralFr: 'Cahiers',
  },
  'amru': {
    gender: 'm',
    singularKab: 'Amru',
    singularFr: 'Stylo',
    pluralKab: 'Imra',
    pluralFr: 'Stylos',
  },
  'imra': {
    gender: 'm',
    singularKab: 'Amru',
    singularFr: 'Stylo',
    pluralKab: 'Imra',
    pluralFr: 'Stylos',
  },
  'taneɣrit': {
    gender: 'f',
    singularKab: 'Taneɣrit',
    singularFr: 'Classe d\'école',
    pluralKab: 'Tineɣriyin',
    pluralFr: 'Classes',
  },
  'tineɣriyin': {
    gender: 'f',
    singularKab: 'Taneɣrit',
    singularFr: 'Classe',
    pluralKab: 'Tineɣriyin',
    pluralFr: 'Classes',
  },
  'aɣerbaz': {
    gender: 'm',
    singularKab: 'Aɣerbaz',
    singularFr: 'École',
    pluralKab: 'Iɣerbazen',
    pluralFr: 'Écoles',
  },
  'iɣerbazen': {
    gender: 'm',
    singularKab: 'Aɣerbaz',
    singularFr: 'École',
    pluralKab: 'Iɣerbazen',
    pluralFr: 'Écoles',
  },
  'awal': {
    gender: 'm',
    singularKab: 'Awal',
    singularFr: 'Mot / Parole',
    pluralKab: 'Awalen',
    pluralFr: 'Mots',
  },
  'awalen': {
    gender: 'm',
    singularKab: 'Awal',
    singularFr: 'Mot',
    pluralKab: 'Awalen',
    pluralFr: 'Mots',
  },
  'asefru': {
    gender: 'm',
    singularKab: 'Asefru',
    singularFr: 'Poème',
    pluralKab: 'Isefra',
    pluralFr: 'Poèmes',
  },
  'isefra': {
    gender: 'm',
    singularKab: 'Asefru',
    singularFr: 'Poème',
    pluralKab: 'Isefra',
    pluralFr: 'Poèmes',
  },
  'aseqsi': {
    gender: 'm',
    singularKab: 'Aseqsi',
    singularFr: 'Question',
    pluralKab: 'Iseqsiyen',
    pluralFr: 'Questions',
  },
  'iseqsiyen': {
    gender: 'm',
    singularKab: 'Aseqsi',
    singularFr: 'Question',
    pluralKab: 'Iseqsiyen',
    pluralFr: 'Questions',
  },
  'tiririt': {
    gender: 'f',
    singularKab: 'Tiririt',
    singularFr: 'Réponse',
    pluralKab: 'Tiririyin',
    pluralFr: 'Réponses',
  },
  'tamacahut': {
    gender: 'f',
    singularKab: 'Tamacahut',
    singularFr: 'Conte / Légende',
    pluralKab: 'Timucuha',
    pluralFr: 'Contes',
  },
  'timucuha': {
    gender: 'f',
    singularKab: 'Tamacahut',
    singularFr: 'Conte',
    pluralKab: 'Timucuha',
    pluralFr: 'Contes',
  },
  'inzi': {
    gender: 'm',
    singularKab: 'Inzi',
    singularFr: 'Proverbe',
    pluralKab: 'Inzan',
    pluralFr: 'Proverbes',
  },
  'inzan': {
    gender: 'm',
    singularKab: 'Inzi',
    singularFr: 'Proverbe',
    pluralKab: 'Inzan',
    pluralFr: 'Proverbes',
  },
  'tadyant': {
    gender: 'f',
    singularKab: 'Tadyant',
    singularFr: 'Aventure / Histoire vécue',
    pluralKab: 'Tidyanin',
    pluralFr: 'Aventures',
  },
  'tidyanin': {
    gender: 'f',
    singularKab: 'Tadyant',
    singularFr: 'Aventure',
    pluralKab: 'Tidyanin',
    pluralFr: 'Aventures',
  },
  'tikkelt': {
    gender: 'f',
    singularKab: 'Tikkelt',
    singularFr: 'Fois',
    pluralKab: 'Tikkal',
    pluralFr: 'Fois',
  },

  // --- Transports & Jeux ---
  'takerrust': {
    gender: 'f',
    singularKab: 'Takerrust',
    singularFr: 'Voiture',
    pluralKab: 'Tikerrusin',
    pluralFr: 'Voitures',
  },
  'aɣerrabu': {
    gender: 'm',
    singularKab: 'Aɣerrabu',
    singularFr: 'Bateau',
    pluralKab: 'Iɣerruba',
    pluralFr: 'Bateaux',
  },
  'asafag': {
    gender: 'm',
    singularKab: 'Asafag',
    singularFr: 'Avion',
    pluralKab: 'Isufag',
    pluralFr: 'Avions',
  },
  'tmacint': {
    gender: 'f',
    singularKab: 'Tmacint',
    singularFr: 'Train',
    pluralKab: 'Tmacinin',
    pluralFr: 'Trains',
  },
  'ddabex': {
    gender: 'm',
    singularKab: 'Ddabex',
    singularFr: 'Ballon',
    pluralKab: 'Ddabux',
    pluralFr: 'Ballons',
  },
  'tacita': {
    gender: 'f',
    singularKab: 'Tacita',
    singularFr: 'Poupée',
    pluralKab: 'Ticitatin',
    pluralFr: 'Poupées',
  },

  // --- Nourriture & Fruits ---
  'tabexsist': {
    gender: 'f',
    singularKab: 'Tabexsist',
    singularFr: 'Figue',
    pluralKab: 'Tibexsisin',
    pluralFr: 'Figues',
  },
  'tibexsisin': {
    gender: 'f',
    singularKab: 'Tabexsist',
    singularFr: 'Figue',
    pluralKab: 'Tibexsisin',
    pluralFr: 'Figues',
  },
  'taẓalimt': {
    gender: 'f',
    singularKab: 'Taẓalimt',
    singularFr: 'Oignon',
    pluralKab: 'Tiẓelmin',
    pluralFr: 'Oignons',
  },
  'tiẓelmin': {
    gender: 'f',
    singularKab: 'Taẓalimt',
    singularFr: 'Oignon',
    pluralKab: 'Tiẓelmin',
    pluralFr: 'Oignons',
  },
  'aḍil': {
    gender: 'm',
    singularKab: 'Aḍil',
    singularFr: 'Raisin',
    pluralKab: 'Iḍilen',
    pluralFr: 'Raisins',
  },
  'xizzu': {
    gender: 'm',
    singularKab: 'Xizzu',
    singularFr: 'Carotte',
    pluralKab: 'Xizzuwat',
    pluralFr: 'Carottes',
  },
  'tuffaḥ': {
    gender: 'm',
    singularKab: 'Tuffaḥ',
    singularFr: 'Pomme',
    pluralKab: 'Tuffaḥat',
    pluralFr: 'Pommes',
  },
  'aɣrum': {
    gender: 'm',
    singularKab: 'Aɣrum',
    singularFr: 'Pain',
    pluralKab: 'Iɣrumen',
    pluralFr: 'Pains',
  },
  'aksum': {
    gender: 'm',
    singularKab: 'Aksum',
    singularFr: 'Viande',
    pluralKab: 'Iksumen',
    pluralFr: 'Viandes',
  },

  // --- Adjectifs (Masculin / Féminin / Pluriel) ---
  'amaynut': {
    gender: 'm',
    feminineKab: 'Tamaynutt',
    pluralKab: 'Imaynuten',
    pluralFr: 'Nouveaux',
  },
  'tamaynutt': {
    gender: 'f',
    masculineKab: 'Amaynut',
    pluralKab: 'Timaynutin',
    pluralFr: 'Nouvelles',
  },
  'aqdim': {
    gender: 'm',
    feminineKab: 'Taqdimt',
    pluralKab: 'Iqdimen',
    pluralFr: 'Anciens / Vieux',
  },
  'taqdimt': {
    gender: 'f',
    masculineKab: 'Aqdim',
    pluralKab: 'Tiqdimin',
    pluralFr: 'Anciennes / Vieilles',
  },
  'ameqqran': {
    gender: 'm',
    feminineKab: 'Tameqqrant',
    pluralKab: 'Imeqqranen',
    pluralFr: 'Grands',
  },
  'tameqqrant': {
    gender: 'f',
    masculineKab: 'Ameqqran',
    pluralKab: 'Timeqqranin',
    pluralFr: 'Grandes',
  },
  'amecṭuḥ': {
    gender: 'm',
    feminineKab: 'Tamecṭuḥt',
    pluralKab: 'Imecṭuḥen',
    pluralFr: 'Petits',
  },
  'tamecṭuḥt': {
    gender: 'f',
    masculineKab: 'Amecṭuḥ',
    pluralKab: 'Timecṭuḥin',
    pluralFr: 'Petites',
  },
  'ameẓyan': {
    gender: 'm',
    feminineKab: 'Tameẓyant',
    pluralKab: 'Imeẓyanen',
    pluralFr: 'Petits',
  },
  'tameẓyant': {
    gender: 'f',
    masculineKab: 'Ameẓyan',
    pluralKab: 'Timeẓyanin',
    pluralFr: 'Petites',
  },
  'aɛeyyan': {
    gender: 'm',
    feminineKab: 'Taɛeyyant',
    pluralKab: 'Iɛeyyanen',
    pluralFr: 'Fatigués',
  },
  'taɛeyyant': {
    gender: 'f',
    masculineKab: 'Aɛeyyan',
    pluralKab: 'Tiɛeyyanin',
    pluralFr: 'Fatiguées',
  },
  'awḥid': {
    gender: 'm',
    feminineKab: 'Tawḥidt',
    pluralKab: 'Iwḥiden',
    pluralFr: 'Seuls',
  },
  'tawḥidt': {
    gender: 'f',
    masculineKab: 'Awḥid',
    pluralKab: 'Tiwḥidin',
    pluralFr: 'Seules',
  },
  'amɣabun': {
    gender: 'm',
    feminineKab: 'Tamɣabunt',
    pluralKab: 'Imɣabunen',
    pluralFr: 'Malheureux',
  },
  'tamɣabunt': {
    gender: 'f',
    masculineKab: 'Amɣabun',
    pluralKab: 'Timɣabunin',
    pluralFr: 'Malheureuses',
  },
  'ukyis': {
    gender: 'm',
    feminineKab: 'Tukyist',
    pluralKab: 'Ukyisen',
    pluralFr: 'Intelligents',
  },
  'tukyist': {
    gender: 'f',
    masculineKab: 'Ukyis',
    pluralKab: 'Tukyisin',
    pluralFr: 'Intelligentes',
  },
  'aḥnin': {
    gender: 'm',
    feminineKab: 'Taḥnint',
    pluralKab: 'Iḥninen',
    pluralFr: 'Affectueux',
  },
  'taḥnint': {
    gender: 'f',
    masculineKab: 'Aḥnin',
    pluralKab: 'Tiḥninin',
    pluralFr: 'Affectueuses',
  },
  'azeggaɣ': {
    gender: 'm',
    feminineKab: 'Tazeggaɣt',
    pluralKab: 'Izeggwaɣen',
    pluralFr: 'Rouges',
  },
  'tazeggaɣt': {
    gender: 'f',
    masculineKab: 'Azeggaɣ',
    pluralKab: 'Tizeggwaɣin',
    pluralFr: 'Rouges',
  },
  'azegzaw': {
    gender: 'm',
    feminineKab: 'Tazegzawt',
    pluralKab: 'Izegzawen',
    pluralFr: 'Verts',
  },
  'tazegzawt': {
    gender: 'f',
    masculineKab: 'Azegzaw',
    pluralKab: 'Tizegzawin',
    pluralFr: 'Vertes',
  },
  'awraɣ': {
    gender: 'm',
    feminineKab: 'Tawraɣt',
    pluralKab: 'Iwraɣen',
    pluralFr: 'Jaunes',
  },
  'tawraɣt': {
    gender: 'f',
    masculineKab: 'Awraɣ',
    pluralKab: 'Tiwraɣin',
    pluralFr: 'Jaunes',
  },
  'amellal': {
    gender: 'm',
    feminineKab: 'Tamellalt',
    pluralKab: 'Imellalen',
    pluralFr: 'Blancs',
  },
  'tamellalt': {
    gender: 'f',
    masculineKab: 'Amellal',
    pluralKab: 'Timellalin',
    pluralFr: 'Blanches',
  },
  'aberkan': {
    gender: 'm',
    feminineKab: 'Taberkant',
    pluralKab: 'Iberkanen',
    pluralFr: 'Noirs',
  },
  'taberkant': {
    gender: 'f',
    masculineKab: 'Aberkan',
    pluralKab: 'Tiberkanin',
    pluralFr: 'Noires',
  },
  'aẓerwal': {
    gender: 'm',
    feminineKab: 'Taẓerwalt',
    pluralKab: 'Iẓerwalen',
    pluralFr: 'Bleus',
  },

  // --- Pronoms & Dialogues genrés ---
  'isem-ik?': {
    gender: 'm',
    feminineKab: 'Isem-im?',
    notes: 'Adressé à un homme (m)',
  },
  'isem-im?': {
    gender: 'f',
    masculineKab: 'Isem-ik?',
    notes: 'Adressé à une femme (f)',
  },
  'd kečč?': {
    gender: 'm',
    feminineKab: 'D kemm?',
    notes: 'Masculin (m)',
  },
  'd kemm?': {
    gender: 'f',
    masculineKab: 'D kečč?',
    notes: 'Féminin (f)',
  },
  'd kunwi': {
    gender: 'm',
    feminineKab: 'D kunmti',
    notes: 'Vous au masculin (m)',
  },
  'd nutni': {
    gender: 'm',
    feminineKab: 'D nutenti',
    notes: 'Ils / Eux au masculin (m)',
  },
  'd nutenti': {
    gender: 'f',
    masculineKab: 'D nutni',
    notes: 'Elles au féminin (f)',
  },
};

/**
 * Normalise un mot kabyle pour la recherche dans la table grammaticale.
 */
export function normalizeKabKey(kab: string): string {
  if (!kab) return '';
  return kab
    .trim()
    .toLowerCase()
    .replace(/[«»"]/g, '')
    .trim();
}

/**
 * Récupère les métadonnées grammaticales d'un mot kabyle ou français.
 */
export function getGrammarEntry(kab: string, fr?: string): GrammarEntry | undefined {
  if (!kab && !fr) return undefined;
  
  if (kab) {
    const rawKey = normalizeKabKey(kab);
    if (KABYLE_GRAMMAR_DB[rawKey]) return KABYLE_GRAMMAR_DB[rawKey];

    // Recherche sans parenthèses (ex: "Axxam (Uxxam)" -> "axxam")
    const cleanKey = normalizeKabKey(rawKey.replace(/\s*\(.*?\)\s*/g, ''));
    if (cleanKey && KABYLE_GRAMMAR_DB[cleanKey]) return KABYLE_GRAMMAR_DB[cleanKey];

    // Recherche par premier mot si plusieurs variantes séparées par / ou virgule
    const firstWord = normalizeKabKey(rawKey.split(/[/,]/)[0]);
    if (firstWord && KABYLE_GRAMMAR_DB[firstWord]) return KABYLE_GRAMMAR_DB[firstWord];
  }

  // Recherche inverse par le mot français
  if (fr) {
    const frClean = fr.toLowerCase().replace(/\s*\(.*?\)\s*/g, '').trim();
    if (frClean) {
      const found = Object.values(KABYLE_GRAMMAR_DB).find(
        (entry) =>
          (entry.singularFr && entry.singularFr.toLowerCase().trim() === frClean) ||
          (entry.pluralFr && entry.pluralFr.toLowerCase().trim() === frClean)
      );
      if (found) return found;
    }
  }

  return undefined;
}

/**
 * Enrichit un WordItem avec ses attributs grammaticaux (genre, singulier, pluriel, etc.)
 */
export function enrichWordWithGrammar(word: WordItem): WordItem {
  if (!word) return word;
  const isCustomOrOverridden = Boolean(
    (word as any).isOverridden ||
    (word as any).isCustom ||
    (word as any).id?.startsWith('custom_') ||
    word.isNew
  );

  const entry = isCustomOrOverridden ? undefined : getGrammarEntry(word.originalKab || word.kab, word.originalFr || word.fr);

  let gender: 'm' | 'f' | 'none' | undefined = undefined;
  if (word.gender === 'm' || word.gender === 'f' || word.gender === 'none') {
    gender = word.gender;
  } else if (word.gender === undefined) {
    if (!isCustomOrOverridden) {
      gender = entry?.gender;
      if (!gender) {
        const rawFr = (word.originalFr || word.fr || '').toLowerCase().trim();
        if (rawFr.includes('(f)') || rawFr.includes('(f.)') || rawFr.includes('(fém)') || rawFr.includes('(féminin)')) {
          gender = 'f';
        } else if (rawFr.includes('(m)') || rawFr.includes('(m.)') || rawFr.includes('(masc)') || rawFr.includes('(masculin)')) {
          gender = 'm';
        }
      }
    }
  }

  // Handle Plural (explicitly empty string or 'none' means NO plural)
  let finalPluralKab: string | undefined = undefined;
  if (word.pluralKab !== undefined) {
    const cleanPl = (word.pluralKab || '').trim();
    if (cleanPl && cleanPl.toLowerCase() !== 'none' && cleanPl.toLowerCase() !== 'aucun') {
      finalPluralKab = cleanPl;
    }
  } else if (!isCustomOrOverridden) {
    finalPluralKab = entry?.pluralKab;
  }

  // Handle Singular (explicitly empty string or 'none' means NO singular)
  let finalSingularKab: string | undefined = undefined;
  if (word.singularKab !== undefined) {
    const cleanSg = (word.singularKab || '').trim();
    if (cleanSg && cleanSg.toLowerCase() !== 'none' && cleanSg.toLowerCase() !== 'aucun') {
      finalSingularKab = cleanSg;
    }
  } else if (!isCustomOrOverridden) {
    finalSingularKab = entry?.singularKab;
  }

  return {
    ...word,
    gender: gender === 'none' ? undefined : (gender ?? word.gender),
    pluralKab: finalPluralKab,
    pluralFr: word.pluralFr !== undefined ? (word.pluralFr.trim() || undefined) : entry?.pluralFr,
    singularKab: finalSingularKab,
    singularFr: word.singularFr !== undefined ? (word.singularFr.trim() || undefined) : entry?.singularFr,
    feminineKab: word.feminineKab !== undefined ? (word.feminineKab.trim() || undefined) : entry?.feminineKab,
    masculineKab: word.masculineKab !== undefined ? (word.masculineKab.trim() || undefined) : entry?.masculineKab,
    conjugationAccompli: word.conjugationAccompli !== undefined ? (word.conjugationAccompli.trim() || undefined) : entry?.conjugationAccompli,
    conjugationInaccompli: word.conjugationInaccompli !== undefined ? (word.conjugationInaccompli.trim() || undefined) : entry?.conjugationInaccompli,
  };
}

export interface QuestionClarification {
  badgeText: string;
  subText: string;
  color: 'blue' | 'pink' | 'amber' | 'emerald';
  icon: string;
  relatedForm?: {
    label: string;
    word: string;
  };
}

/**
 * Anciennes pastilles de clarification (désactivées définitivement).
 */
export function getQuestionClarification(
  _targetWord: WordItem,
  _direction: 'FR_TO_KAB' | 'KAB_TO_FR' | 'AUDIO_TO_FR' = 'FR_TO_KAB',
  _isAdventureMode: boolean = false
): QuestionClarification | null {
  return null;
}

export interface RelatedNumberForm {
  type: 'pl' | 'sg';
  label: string;
  kab: string;
  fr?: string;
  display: string;
}

/**
 * Supprime les mentions de pluriel ou singulier entre parenthèses d'une chaîne
 * ex: "Brebis / Moutons (pl.)" -> "Brebis / Moutons"
 * ex: "Cailloux (pluriel)" -> "Cailloux"
 */
export function stripGrammarParentheses(text: string): string {
  if (!text) return '';
  return text
    .replace(/\s*\((?:pl\.?|pluriel|sg\.?|singulier)\)/gi, '')
    .trim();
}

/**
 * Récupère la forme au pluriel ou au singulier inscrite dans le dictionnaire pour un mot donné,
 * afin de l'afficher directement (ex: "Pluriel : Iblaḍen" ou "Singulier : Ablaḍ")
 * sans parenthèses et sans donner la traduction française.
 */
export function getRelatedNumberForm(
  word: WordItem | null | undefined,
  _isFrenchPrompt: boolean = false
): RelatedNumberForm | null {
  if (!word) return null;
  const enriched = enrichWordWithGrammar(word);
  const kabRaw = (enriched.originalKab || enriched.kab || '').trim();
  const kabLower = normalizeKabKey(kabRaw);
  const frLower = (enriched.originalFr || enriched.fr || '').toLowerCase().trim();

  const isPlural =
    (enriched.singularKab && normalizeKabKey(enriched.singularKab) !== kabLower) ||
    frLower.includes('(pl.') ||
    frLower.includes('(pluriel)') ||
    frLower.includes('(pl)');

  if (isPlural) {
    if (enriched.singularKab && normalizeKabKey(enriched.singularKab) !== kabLower) {
      const kab = enriched.singularKab;
      const display = `Singulier : ${kab}`;
      return {
        type: 'sg',
        label: 'Singulier',
        kab,
        display,
      };
    }
  } else {
    if (enriched.pluralKab && normalizeKabKey(enriched.pluralKab) !== kabLower) {
      const kab = enriched.pluralKab;
      const display = `Pluriel : ${kab}`;
      return {
        type: 'pl',
        label: 'Pluriel',
        kab,
        display,
      };
    }
  }

  return null;
}

/**
 * Formate le texte d'un mot pour l'affichage dans les questions de quiz et défis
 * en veillant à NE PAS afficher de mention (pl.) ou (sg.) entre parenthèses,
 * et à ne donner aucune traduction française incidente.
 */
export function formatPromptWithGrammar(word: WordItem | null | undefined, isFrenchPrompt: boolean): string {
  if (!word) return '';
  const rawText = isFrenchPrompt ? (word.fr || '') : (word.kab || '');
  if (!rawText) return '';
  return stripGrammarParentheses(rawText);
}


